import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { drawdownAPI, fundAPI, investorAccountAPI } from '../../services/api';
import ReceiptRemarksHint from '../../components/drawdowns/ReceiptRemarksHint';
import RecipientPicker from '../../components/drawdowns/RecipientPicker';
import { ArrowLeft, Edit, Save, X, Check, DollarSign, Users, BarChart3, Calendar, Percent, Trash2, Plus, FileText, FileOutput, Loader2, Search, Download, CheckCircle, Eye, FileSpreadsheet, AlertTriangle, RefreshCw, Columns, Mail, ClipboardCheck, Bell, Lock, Shield, Upload, Link2, Copy, Send, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDateFormat } from '../../hooks/useDateFormat';
import DrawdownEmailsTab from './DrawdownEmailsTab';
import DrawdownAckTab from './DrawdownAckTab';
import DrawdownRemindersTab from './DrawdownRemindersTab';

// Field sections for report column picker (mirrors Investor Account submodule sections exactly)
const REPORT_FIELD_SECTIONS = {
  account_info: {
    label: 'Account Info',
    fields: [
      { key: 'ppm_no', label: 'PPM No' },
      { key: 'type_of_investor', label: 'Type of Investor' },
      { key: 'pan', label: 'PAN' },
      { key: 'date_of_birth_or_incorporation', label: 'DOB / Incorporation Date' },
      { key: 'contact_name', label: 'Linked Contact' },
      { key: 'law_under_formed', label: 'Law Under Formed' },
      { key: 'investor_lead_name', label: 'Investor Lead' },
      { key: 'distributor_name', label: 'Distributor' },
    ],
  },
  primary_contacts: {
    label: 'Primary Contacts',
    fields: [
      { key: 'primary_contact_name', label: 'Name' },
      { key: 'primary_contact_email', label: 'Email' },
      { key: 'primary_contact_mobile', label: 'Mobile' },
      { key: 'primary_contact_telephone', label: 'Telephone' },
      { key: 'primary_contact_signatory', label: 'Authorised Signatory' },
      { key: 'primary_contact_signatory_position', label: 'Signatory Position' },
    ],
  },
  additional_contacts: {
    label: 'Additional Contacts',
    fields: [
      { key: 'additional_contact_names', label: 'Names' },
      { key: 'additional_contact_emails', label: 'Emails' },
      { key: 'additional_contact_mobiles', label: 'Mobiles' },
    ],
  },
  correspondence_address: {
    label: 'Correspondence Address',
    fields: [
      { key: 'correspondence_address', label: 'Address' },
      { key: 'correspondence_city', label: 'City' },
      { key: 'correspondence_state', label: 'State' },
      { key: 'correspondence_country', label: 'Country' },
      { key: 'correspondence_zip', label: 'PIN Code' },
    ],
  },
  registered_address: {
    label: 'Registered / Permanent Address',
    fields: [
      { key: 'registered_address', label: 'Address' },
      { key: 'registered_city', label: 'City' },
      { key: 'registered_state', label: 'State' },
      { key: 'registered_country', label: 'Country' },
      { key: 'registered_zip', label: 'PIN Code' },
    ],
  },
  fatca_individual: {
    label: 'FATCA / CRS (Individual)',
    fields: [
      { key: 'fatca_place_of_birth', label: 'Place of Birth' },
      { key: 'fatca_country_of_birth', label: 'Country of Birth' },
      { key: 'fatca_country_of_citizenship', label: 'Country of Citizenship' },
      { key: 'fatca_us_citizen', label: 'US Citizen/Resident' },
      { key: 'fatca_us_tin', label: 'US TIN' },
      { key: 'fatca_tax_residency', label: 'Tax Residency Country' },
      { key: 'fatca_tin_type', label: 'TIN Type' },
      { key: 'fatca_tin', label: 'TIN' },
    ],
  },
  fatca_non_individual: {
    label: 'FATCA / CRS (Non-Individual)',
    fields: [
      { key: 'fatca_ni_is_us_person', label: 'Is US Person' },
      { key: 'fatca_ni_us_tin', label: 'US TIN' },
      { key: 'fatca_ni_formed_outside_india', label: 'Formed Outside India' },
      { key: 'fatca_ni_tax_residency_other', label: 'Tax Residency Other Than India' },
      { key: 'fatca_ni_is_financial_institution', label: 'Financial Institution' },
      { key: 'fatca_ni_giin', label: 'GIIN' },
      { key: 'fatca_ni_is_publicly_traded', label: 'Publicly Traded' },
      { key: 'fatca_ni_controlling_persons', label: 'Controlling Persons Not Indian' },
    ],
  },
};
const ALL_REPORT_FIELDS = Object.values(REPORT_FIELD_SECTIONS).flatMap(s => s.fields);

const DrawdownDetail = () => {
  const fmtDate = useDateFormat();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const [drawdown, setDrawdown] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryForm, setSummaryForm] = useState({});

  // Receipt modal
  const [receiptModal, setReceiptModal] = useState(null); // investor object
  const [receiptForm, setReceiptForm] = useState({ amount: '', date_of_receipt: '' });
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  const [fundClasses, setFundClasses] = useState([]);

  // Edit receipt
  const [editingReceipt, setEditingReceipt] = useState(null); // { investorId, receipt }
  const [editReceiptForm, setEditReceiptForm] = useState({});

  // Report state
  const [reportFilter, setReportFilter] = useState('all');
  // Phase R — Email Report modal state
  const [emailReportOpen, setEmailReportOpen] = useState(false);
  const [emailReportForm, setEmailReportForm] = useState({
    to: [], cc: [], format: 'excel', subject: '', message: '',
    extToInput: '', extCcInput: '',
  });
  const [emailReportSending, setEmailReportSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [canEmailExternal, setCanEmailExternal] = useState(false);

  // WhatsApp Report modal state
  const [waReportOpen, setWaReportOpen] = useState(false);
  const [waReportLoading, setWaReportLoading] = useState(false);
  const [waReportSending, setWaReportSending] = useState(false);
  const [waReportPreflight, setWaReportPreflight] = useState(null);
  const [waReportForm, setWaReportForm] = useState({
    template_id: '', recipient_user_ids: [], format: 'pdf',
  });
  const [report, setReport] = useState(null);
  const [reportExtraFields, setReportExtraFields] = useState(() => {
    try {
      const saved = localStorage.getItem('drawdownReport_extraFields');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showReportFieldPicker, setShowReportFieldPicker] = useState(false);

  // Edit investor amount
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [editInvAmount, setEditInvAmount] = useState('');

  // Investor search
  const [investorSearch, setInvestorSearch] = useState('');

  // Expanded receipts rows
  const [expandedRows, setExpandedRows] = useState({});

  // Phase M2 — Add Investor modal + maker-checker handlers
  const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
  const [eligibleAccounts, setEligibleAccounts] = useState([]);
  const [addInvForm, setAddInvForm] = useState({ account_id: '', class_name: '' });
  const [addInvSubmitting, setAddInvSubmitting] = useState(false);

  // Documents tab state
  const [fundTemplates, setFundTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedInvestorIds, setSelectedInvestorIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // Format options
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  const [fileNamingPattern, setFileNamingPattern] = useState('');
  const [consolidateByAccount, setConsolidateByAccount] = useState(false);
  // PDF password protection
  const [passwordMode, setPasswordMode] = useState('none'); // 'none' | 'single_field' | 'combination' | 'csv'
  const [passwordField, setPasswordField] = useState('pan');
  const [passwordExpression, setPasswordExpression] = useState('lower(first(name,4)) + ddmm(dob)');
  const [passwordCsvFile, setPasswordCsvFile] = useState(null); // File
  const [passwordCsvBase64, setPasswordCsvBase64] = useState(''); // base64 string

  const handlePasswordCsvUpload = (file) => {
    if (!file) {
      setPasswordCsvFile(null);
      setPasswordCsvBase64('');
      return;
    }
    setPasswordCsvFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:...;base64,XXXX"
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',', 2)[1] : result;
      setPasswordCsvBase64(base64);
    };
    reader.readAsDataURL(file);
  };
  // Generated notices
  const [notices, setNotices] = useState([]);
  const [loadingNotices, setLoadingNotices] = useState(false);
  const [docView, setDocView] = useState('generate'); // 'generate' | 'notices'
  const [selectedNoticeIds, setSelectedNoticeIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(null); // 'docx' | 'pdf' | null
  const [bulkReviewing, setBulkReviewing] = useState(false);
  // Generation job polling
  const [generationJob, setGenerationJob] = useState(null); // { job_id, status, total, completed, failed }

  useEffect(() => { loadData(); }, [id]);

  // Persist extra field selection
  useEffect(() => {
    localStorage.setItem('drawdownReport_extraFields', JSON.stringify(reportExtraFields));
  }, [reportExtraFields]);

  const loadData = async () => {
    try {
      const [ddRes, invRes] = await Promise.all([
        drawdownAPI.getById(id),
        drawdownAPI.getInvestors(id),
      ]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      // Pull fund classes so the Record Receipt modal can auto-populate
      // ISIN and Units from the chosen class.
      if (ddRes.data?.fund_id) {
        try {
          const fc = await fundAPI.getClasses(ddRes.data.fund_id);
          setFundClasses(fc.data || []);
        } catch { setFundClasses([]); }
      }
      setSummaryForm({
        drawdown_name: ddRes.data.drawdown_name,
        percentage_called: ddRes.data.percentage_called,
        notice_date: ddRes.data.notice_date || '',
        due_date: ddRes.data.due_date || '',
        receipt_remarks_template: ddRes.data.receipt_remarks_template || '',
        password_hint: ddRes.data.password_hint || '',
        password_hint_email: ddRes.data.password_hint_email || '',
        status: ddRes.data.status,
        custom_fields: (ddRes.data.custom_fields || []).map(cf => ({
          field_name: cf.field_name || '',
          field_type: cf.field_type || 'text',
          field_value: cf.field_value ?? '',
          options: cf.options || [],
          field_key: cf.field_key,
        })),
      });
      // Auto-expand any investor row that has at least one pending-approval
      // tranche so the user immediately sees the Approve/Reject buttons.
      const autoExpand = {};
      (invRes.data || []).forEach(inv => {
        const hasPending = (inv.receipts || []).some(r => (r.approval_status || 'approved') === 'pending_approval');
        if (hasPending) autoExpand[inv.id] = true;
      });
      setExpandedRows(prev => ({ ...prev, ...autoExpand }));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUpdateSummary = async () => {
    try {
      const payload = { ...summaryForm };
      // For class-wise drawdowns the top-level `percentage_called` is always 0
      // (each class carries its own %). Stripping it avoids the backend's legacy
      // recompute path wiping all investor drawdown amounts to zero.
      if (drawdown?.class_percentages && drawdown.class_percentages.length > 0) {
        delete payload.percentage_called;
      } else {
        payload.percentage_called = parseFloat(summaryForm.percentage_called) || 0;
      }
      const res = await drawdownAPI.update(id, payload);
      setDrawdown(res.data);
      setEditingSummary(false);
      const invRes = await drawdownAPI.getInvestors(id);
      setInvestors(invRes.data);
    } catch { alert('Failed to update'); }
  };

  const handleSaveInvestorAmount = async (invId) => {
    try {
      await drawdownAPI.updateInvestor(id, invId, { drawdown_amount: parseFloat(editInvAmount) || 0 });
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      setEditingInvestor(null);
    } catch { alert('Failed to update'); }
  };

  const handleRecordReceipt = async () => {
    if (!receiptModal || !receiptForm.amount || !receiptForm.date_of_receipt) return;
    setSubmittingReceipt(true);
    try {
      await drawdownAPI.recordReceipt(id, receiptModal.id, {
        amount: parseFloat(receiptForm.amount),
        date_of_receipt: receiptForm.date_of_receipt,
      });
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      setReceiptModal(null);
      setReceiptForm({ amount: '', date_of_receipt: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmittingReceipt(false); }
  };

  const handleEditReceipt = async () => {
    if (!editingReceipt) return;
    try {
      await drawdownAPI.editReceipt(id, editingReceipt.investorId, editingReceipt.receipt.id, {
        amount: parseFloat(editReceiptForm.amount) || undefined,
        date_of_receipt: editReceiptForm.date_of_receipt || undefined,
        soa_remarks: editReceiptForm.soa_remarks,
      });
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      setEditingReceipt(null);
    } catch { alert('Failed to update receipt'); }
  };

  const handleDeleteReceipt = async (investorId, receiptId) => {
    if (!window.confirm('Delete this receipt? The linked transaction will also be removed.')) return;
    try {
      await drawdownAPI.deleteReceipt(id, investorId, receiptId);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
    } catch { alert('Failed to delete'); }
  };

  // ---------- Maker-Checker (Phase L5) ----------
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null); // { kind: 'drawdown'|'receipt', investorId?, receiptId?, reason: '' }

  const handleSubmitForApproval = async () => {
    if (!window.confirm('Submit this drawdown for approval? You will not be able to edit until it is approved or rejected.')) return;
    setActionLoading(true);
    try {
      await drawdownAPI.submitForApproval(id);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit for approval'); }
    finally { setActionLoading(false); }
  };

  const handleApproveDrawdown = async () => {
    if (!window.confirm('Approve this drawdown? Amount Called transactions will be auto-created for every investor row.')) return;
    setActionLoading(true);
    try {
      await drawdownAPI.approveDrawdown(id);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to approve'); }
    finally { setActionLoading(false); }
  };

  const handleRejectDrawdown = async () => {
    setActionLoading(true);
    try {
      await drawdownAPI.rejectDrawdown(id, rejectModal?.reason);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      setRejectModal(null);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reject'); }
    finally { setActionLoading(false); }
  };

  const handleApproveReceipt = async (invId, receiptId) => {
    if (!window.confirm('Approve this receipt? The corresponding Amount Received transaction will be created in the investor account.')) return;
    setActionLoading(true);
    try {
      await drawdownAPI.approveReceipt(id, invId, receiptId);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to approve receipt'); }
    finally { setActionLoading(false); }
  };

  const handleRejectReceipt = async () => {
    if (!rejectModal || rejectModal.kind !== 'receipt') return;
    setActionLoading(true);
    try {
      await drawdownAPI.rejectReceipt(id, rejectModal.investorId, rejectModal.receiptId, rejectModal.reason);
      const [ddRes, invRes] = await Promise.all([drawdownAPI.getById(id), drawdownAPI.getInvestors(id)]);
      setDrawdown(ddRes.data);
      setInvestors(invRes.data);
      setRejectModal(null);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reject receipt'); }
    finally { setActionLoading(false); }
  };


  const loadReport = async (filter) => {
    setReportFilter(filter);
    try {
      const res = await drawdownAPI.getReport(id, filter);
      setReport(res.data);
    } catch { setReport(null); }
  };

  useEffect(() => {
    if (activeTab === 'reports' && !report) loadReport('all');
  }, [activeTab]);

  const toggleRow = (invId) => setExpandedRows(prev => ({ ...prev, [invId]: !prev[invId] }));

  // --- Phase M2: Maker-Checker add / delete handlers --------------------------
  const openAddInvestorModal = async () => {
    setShowAddInvestorModal(true);
    setAddInvForm({ account_id: '', class_name: '' });
    // Eligible = accounts that hold the drawdown's fund and aren't already on it.
    // Filter happens client-side because the existing list endpoint already
    // returns each account with its `investments` array; no need for a new API.
    try {
      const res = await investorAccountAPI.getAll();
      const accts = Array.isArray(res.data) ? res.data : (res.data.items || res.data.accounts || []);
      const existing = new Set(investors.map(i => `${i.account_id}|${i.class_name || ''}`));
      // Phase O3 — restrict eligibility to the classes this drawdown actually
      // covers when it's a class-wise drawdown.
      const classScope = (drawdown?.class_percentages || []).map(cp => cp.class_name).filter(Boolean);
      const enriched = accts.map(a => {
        const classes = (a.investments || [])
          .filter(inv => inv.fund_id === drawdown?.fund_id)
          .flatMap(inv => (inv.class_commitments || []).map(c => c.class_name))
          .filter(Boolean);
        // Dedup class names from re-ups, then keep only those in scope (if any)
        let uniqClasses = Array.from(new Set(classes));
        if (classScope.length > 0) {
          uniqClasses = uniqClasses.filter(cn => classScope.includes(cn));
        }
        const holdsFund = (a.investments || []).some(inv => inv.fund_id === drawdown?.fund_id);
        return { ...a, _classes_in_fund: uniqClasses, _holds_fund: holdsFund };
      });
      const filtered = enriched.filter(a => {
        if (!a._holds_fund) return false;
        if ((drawdown?.class_percentages && drawdown.class_percentages.length > 0) || a._classes_in_fund.length > 0) {
          const remaining = a._classes_in_fund.filter(cn => !existing.has(`${a.id}|${cn}`));
          return remaining.length > 0;
        }
        return !existing.has(`${a.id}|`);
      });
      setEligibleAccounts(filtered);
    } catch { setEligibleAccounts([]); }
  };

  const submitAddInvestor = async () => {
    if (!addInvForm.account_id) return alert('Pick an account');
    const isClasswise = drawdown?.class_percentages && drawdown.class_percentages.length > 0;
    const acct = eligibleAccounts.find(a => a.id === addInvForm.account_id);
    if ((isClasswise || acct?._classes_in_fund?.length) && !addInvForm.class_name) return alert('Pick a class');
    setAddInvSubmitting(true);
    try {
      await drawdownAPI.submitAddInvestor(id, {
        account_id: addInvForm.account_id,
        class_name: addInvForm.class_name || null,
      });
      setShowAddInvestorModal(false);
      await loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit add');
    } finally { setAddInvSubmitting(false); }
  };

  const handleSubmitDeleteInvestor = async (invId, name, cls) => {
    const label = cls ? `${name} (${cls})` : name;
    if (!window.confirm(`Submit a request to delete ${label} from this drawdown? Another user will need to approve before the row is removed.`)) return;
    try {
      const res = await drawdownAPI.submitDeleteInvestor(id, invId);
      await loadData();
      if (res.data?.status === 'withdrawn') {
        alert(`${label} removed (the row was never approved).`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit delete');
    }
  };

  const handleApproveAddInvestor = async (invId) => {
    if (!window.confirm('Approve this addition? Once approved the row will count toward the drawdown totals.')) return;
    try { await drawdownAPI.approveAddInvestor(id, invId); await loadData(); }
    catch (e) { alert(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleApproveDeleteInvestor = async (invId) => {
    if (!window.confirm('Approve this deletion? The investor row will be permanently removed and totals will adjust.')) return;
    try { await drawdownAPI.approveDeleteInvestor(id, invId); await loadData(); }
    catch (e) { alert(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleRejectInvestorAction = async (invId, action) => {
    const msg = action === 'add'
      ? 'Reject this addition? The proposed row will be discarded.'
      : 'Cancel this delete request? The row stays in the drawdown.';
    if (!window.confirm(msg)) return;
    try { await drawdownAPI.rejectInvestorAction(id, invId); await loadData(); }
    catch (e) { alert(e.response?.data?.detail || 'Reject failed'); }
  };
  // ----------------------------------------------------------------------------

  // Documents tab logic
  const loadTemplates = async () => {
    if (fundTemplates.length > 0) return;
    setLoadingTemplates(true);
    try {
      const res = await drawdownAPI.getTemplates(id);
      setFundTemplates(res.data);
    } catch { setFundTemplates([]); }
    finally { setLoadingTemplates(false); }
  };

  const loadNotices = async () => {
    setLoadingNotices(true);
    try {
      const res = await drawdownAPI.getNotices(id);
      setNotices(res.data);
    } catch { setNotices([]); }
    finally { setLoadingNotices(false); }
  };

  useEffect(() => {
    if (activeTab === 'documents' || activeTab === 'investors') {
      loadTemplates();
      loadNotices();
      // Check for any running generation job
      checkRunningGeneration();
    }
  }, [activeTab]);

  const checkRunningGeneration = async () => {
    try {
      const res = await drawdownAPI.getGenerationStatus(id);
      if (res.data && res.data.status === 'running') {
        setGenerationJob(res.data);
        setGenerating(true);
        pollGenerationStatus(res.data.job_id);
      }
    } catch { /* ignore */ }
  };

  const toggleInvestorSelection = (invId) => {
    setSelectedInvestorIds(prev =>
      prev.includes(invId) ? prev.filter(x => x !== invId) : [...prev, invId]
    );
  };

  const filteredDocInvestors = investors.filter(inv =>
    !docSearch || (inv.account_name || '').toLowerCase().includes(docSearch.toLowerCase()) ||
    (inv.class_name || '').toLowerCase().includes(docSearch.toLowerCase()) ||
    (inv.folio_no || '').toLowerCase().includes(docSearch.toLowerCase())
  );

  const filteredInvestors = investors.filter(inv => {
    if (!investorSearch) return true;
    const q = investorSearch.toLowerCase();
    return (inv.account_name || '').toLowerCase().includes(q) ||
      (inv.class_name || '').toLowerCase().includes(q) ||
      (inv.ppm_no || '').toLowerCase().includes(q) ||
      (inv.folio_no || '').toLowerCase().includes(q);
  });

  const toggleSelectAllInvestors = () => {
    const allIds = filteredDocInvestors.map(i => i.id);
    const allSelected = allIds.every(id => selectedInvestorIds.includes(id));
    if (allSelected) {
      setSelectedInvestorIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedInvestorIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  // Build password protection payload from current state (returns null if disabled)
  const buildPasswordPayload = () => {
    if (passwordMode === 'none' || !passwordMode) return {};
    const payload = { password_mode: passwordMode };
    if (passwordMode === 'single_field') {
      payload.password_field = passwordField;
    } else if (passwordMode === 'combination') {
      payload.password_expression = passwordExpression;
    } else if (passwordMode === 'csv') {
      payload.password_csv_b64 = passwordCsvBase64;
      payload.password_csv_filename = passwordCsvFile?.name || 'passwords.csv';
    }
    return payload;
  };

  const handleGenerateNotices = async () => {
    if (!selectedTemplate || selectedInvestorIds.length === 0) return;
    if (passwordMode === 'csv' && !passwordCsvBase64) {
      alert('Please upload a Password CSV before generating.');
      return;
    }
    setGenerating(true);
    try {
      const res = await drawdownAPI.generateNotices(id, {
        template_id: selectedTemplate.id,
        investor_ids: selectedInvestorIds,
        date_format: dateFormat,
        number_format: numberFormat,
        file_naming_pattern: fileNamingPattern,
        consolidate_by_account: consolidateByAccount,
        ...buildPasswordPayload(),
      });
      // Start polling for generation progress
      const job = res.data;
      setGenerationJob(job);
      setSelectedInvestorIds([]);
      pollGenerationStatus(job.job_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Notice generation failed');
      setGenerating(false);
    }
  };

  const pollGenerationStatus = async (jobId) => {
    const poll = async () => {
      try {
        const res = await drawdownAPI.getGenerationStatus(id);
        const job = res.data;
        setGenerationJob(job);
        if (job.status === 'running') {
          setTimeout(poll, 2000);
        } else {
          // Generation finished (completed/partial/failed)
          setGenerating(false);
          await loadNotices();
          setDocView('notices');
        }
      } catch (err) {
        console.error('Polling error:', err);
        setGenerating(false);
        setGenerationJob(null);
        await loadNotices();
        setDocView('notices');
      }
    };
    setTimeout(poll, 2000);
  };

  const handleRetryFailed = async () => {
    if (!generationJob?.failed_accounts?.length || !selectedTemplate) return;
    const failedInvestorIds = generationJob.failed_accounts.map(a => a.investor_id);
    setGenerating(true);
    try {
      const res = await drawdownAPI.generateNotices(id, {
        template_id: selectedTemplate.id,
        investor_ids: failedInvestorIds,
        date_format: dateFormat,
        number_format: numberFormat,
        file_naming_pattern: fileNamingPattern,
        consolidate_by_account: consolidateByAccount,
        ...buildPasswordPayload(),
      });
      const job = res.data;
      setGenerationJob(job);
      pollGenerationStatus(job.job_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Retry failed');
      setGenerating(false);
    }
  };

  const handleDownloadNotice = async (notice, format, protectedFlag = false) => {
    try {
      const res = await drawdownAPI.downloadNotice(notice.id, format, protectedFlag);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = protectedFlag ? ' (protected)' : '';
      a.download = `${notice.file_name}${suffix}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleReviewNotice = async (noticeId) => {
    try {
      await drawdownAPI.reviewNotice(noticeId);
      await loadNotices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Review failed');
    }
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('Delete this generated notice?')) return;
    try {
      await drawdownAPI.deleteNotice(noticeId);
      setNotices(prev => prev.filter(n => n.id !== noticeId));
      setSelectedNoticeIds(prev => prev.filter(id => id !== noticeId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleNoticeSelection = (nid) => {
    setSelectedNoticeIds(prev => prev.includes(nid) ? prev.filter(x => x !== nid) : [...prev, nid]);
  };

  const toggleSelectAllNotices = () => {
    const allIds = notices.map(n => n.id);
    const allSelected = allIds.every(id => selectedNoticeIds.includes(id));
    setSelectedNoticeIds(allSelected ? [] : allIds);
  };

  const handleBulkDownload = async (format, protectedFlag = false) => {
    setBulkDownloading(protectedFlag ? `${format}-protected` : format);
    try {
      const res = await drawdownAPI.bulkDownloadNotices(id, format, protectedFlag);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = protectedFlag ? ' Protected' : '';
      a.download = `${drawdown?.drawdown_number || 'DD'} - All Notices${suffix} (${format.toUpperCase()}).zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // responseType: 'blob' means error data is also a Blob, need to parse it
      let message = 'Bulk download failed';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message = json.detail || message;
        } catch { /* ignore parse error */ }
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      }
      alert(message);
    } finally {
      setBulkDownloading(null);
    }
  };

  const [bulkDeletingNotices, setBulkDeletingNotices] = useState(false);

  const handleBulkDeleteNotices = async () => {
    if (selectedNoticeIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedNoticeIds.length} notice(s)? This will remove the generated documents permanently.`)) return;
    setBulkDeletingNotices(true);
    try {
      await drawdownAPI.bulkDeleteNotices(id, selectedNoticeIds);
      await loadNotices();
      setSelectedNoticeIds([]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeletingNotices(false);
    }
  };

  const handleBulkReview = async () => {
    const pendingIds = selectedNoticeIds.filter(nid => {
      const n = notices.find(x => x.id === nid);
      return n && !n.reviewed;
    });
    if (pendingIds.length === 0) return;
    setBulkReviewing(true);
    try {
      await drawdownAPI.bulkReviewNotices(id, pendingIds);
      await loadNotices();
      setSelectedNoticeIds([]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk review failed');
    } finally {
      setBulkReviewing(false);
    }
  };

  // Report export
  const [exporting, setExporting] = useState(null);
  const handleExportReport = async (format) => {
    setExporting(format);
    try {
      const res = await drawdownAPI.exportReport(id, format, reportFilter, reportExtraFields);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${drawdown?.drawdown_number || 'DD'} - Report (${reportFilter}).${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const toggleReportField = (key) => {
    setReportExtraFields(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const toggleReportSection = (sectionKey) => {
    const sectionFields = REPORT_FIELD_SECTIONS[sectionKey].fields.map(f => f.key);
    const allSelected = sectionFields.every(k => reportExtraFields.includes(k));
    if (allSelected) {
      setReportExtraFields(prev => prev.filter(k => !sectionFields.includes(k)));
    } else {
      setReportExtraFields(prev => [...new Set([...prev, ...sectionFields])]);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!drawdown) return <div className="p-8 text-center text-red-500">Drawdown not found</div>;

  const tabs = [
    { id: 'summary', label: 'Summary', icon: BarChart3 },
    { id: 'investors', label: `Investors (${investors.length})`, icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'reminders', label: 'Reminders', icon: Bell },
    { id: 'acknowledgments', label: 'Acknowledgments', icon: ClipboardCheck },
  ];

  const statusColors = {
    draft: 'bg-gray-100 text-gray-600',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    active: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
  };
  const statusLabel = (s) => (s || '').replace(/_/g, ' ');
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const makerId = drawdown.submitted_by_user_id || drawdown.created_by_user_id;
  const canApproveDrawdown = isAdmin || (isManager && user?.id && makerId && makerId !== user?.id);
  const pctReceived = drawdown.total_drawdown_amount > 0 ? ((drawdown.total_received_amount / drawdown.total_drawdown_amount) * 100).toFixed(1) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="drawdown-detail-page">
      <button onClick={() => navigate('/drawdowns')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to Drawdowns
      </button>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{drawdown.drawdown_name}</h1>
          <p className="text-sm text-gray-500 mt-1">{drawdown.drawdown_number} &middot; {drawdown.fund_name} &middot; {drawdown.currency || '-'}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${statusColors[drawdown.status]}`} data-testid="dd-status">{statusLabel(drawdown.status)}</span>
          {canEdit && drawdown.status === 'draft' && (
            <button onClick={handleSubmitForApproval} disabled={actionLoading}
              className="px-3 py-1 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="submit-for-approval-btn">
              <FileText size={14} /> Submit for Approval
            </button>
          )}
          {canEdit && drawdown.status === 'rejected' && (
            <button onClick={handleSubmitForApproval} disabled={actionLoading}
              className="px-3 py-1 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="resubmit-for-approval-btn">
              <RefreshCw size={14} /> Re-submit
            </button>
          )}
          {drawdown.status === 'pending_approval' && canApproveDrawdown && (
            <>
              <button onClick={handleApproveDrawdown} disabled={actionLoading}
                className="px-3 py-1 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="approve-drawdown-btn">
                <Check size={14} /> Approve
              </button>
              <button onClick={() => setRejectModal({ kind: 'drawdown', reason: '' })} disabled={actionLoading}
                className="px-3 py-1 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="reject-drawdown-btn">
                <X size={14} /> Reject
              </button>
            </>
          )}
          {drawdown.status === 'pending_approval' && !canApproveDrawdown && isManager && (
            <span className="px-2 py-1 text-xs rounded bg-amber-50 border border-amber-200 text-amber-800">Awaiting another approver</span>
          )}
        </div>
      </div>
      {drawdown.status === 'rejected' && drawdown.rejection_reason && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="rejection-reason-banner">
          <strong>Rejected:</strong> {drawdown.rejection_reason}
        </div>
      )}
      {drawdown.status === 'pending_approval' && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="pending-approval-banner">
          <AlertTriangle size={14} className="inline mr-1" />
          This drawdown is awaiting approval. Editing it will revert it to pending and void any Amount Called transactions until re-approved.
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase">% Called</p>
          {drawdown.class_percentages && drawdown.class_percentages.length > 0 ? (
            <div className="mt-1 space-y-0.5" data-testid="dd-class-percentages-summary">
              {drawdown.class_percentages.map((cp, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 font-medium">{cp.class_name}</span>
                  <span className="text-blue-600 font-bold">{cp.percentage_called}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xl font-bold text-blue-600">{drawdown.percentage_called}%</p>
          )}
        </div>
        <div className="bg-white border rounded-lg p-4"><p className="text-xs text-gray-500 uppercase">Total Drawdown</p><p className="text-xl font-bold text-gray-900">{(drawdown.total_drawdown_amount || 0).toLocaleString()}</p></div>
        <div className="bg-white border rounded-lg p-4"><p className="text-xs text-gray-500 uppercase">Total Received</p><p className="text-xl font-bold text-green-600">{(drawdown.total_received_amount || 0).toLocaleString()}</p></div>
        <div className="bg-white border rounded-lg p-4"><p className="text-xs text-gray-500 uppercase">Outstanding</p><p className="text-xl font-bold text-red-500">{((drawdown.total_drawdown_amount || 0) - (drawdown.total_received_amount || 0)).toLocaleString()}</p></div>
      </div>

      {/* Class-wise breakdown — only when this drawdown was generated class-wise */}
      {drawdown.class_percentages && drawdown.class_percentages.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden mb-6" data-testid="dd-classwise-table">
          <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Class-wise Drawdown</h3>
            {canEdit && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Recompute every investor\'s drawdown amount from the stored class percentages? This overwrites non-overridden amounts.')) return;
                  try {
                    const res = await drawdownAPI.recomputeAmounts(id);
                    setDrawdown(res.data);
                    const invRes = await drawdownAPI.getInvestors(id);
                    setInvestors(invRes.data);
                  } catch { alert('Failed to recompute'); }
                }}
                className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                data-testid="dd-recompute-amounts-btn"
                title="Re-apply the stored class percentages to every investor commitment"
              >
                Recompute Amounts
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Class</th>
                <th className="px-4 py-2 text-right font-medium">% Called</th>
                <th className="px-4 py-2 text-right font-medium">Class Commitment</th>
                <th className="px-4 py-2 text-right font-medium">Drawdown Amount</th>
                <th className="px-4 py-2 text-right font-medium">Received</th>
                <th className="px-4 py-2 text-right font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const byClass = {};
                (drawdown.class_percentages || []).forEach(cp => {
                  byClass[cp.class_name] = { class_name: cp.class_name, percentage: cp.percentage_called, commit: 0, drawdown: 0, received: 0 };
                });
                (investors || []).forEach(inv => {
                  const k = inv.class_name || '-';
                  if (!byClass[k]) byClass[k] = { class_name: k, percentage: null, commit: 0, drawdown: 0, received: 0 };
                  byClass[k].commit += inv.commitment_amount || 0;
                  byClass[k].drawdown += inv.drawdown_amount || 0;
                  byClass[k].received += inv.received_amount || 0;
                });
                const rows = Object.values(byClass);
                return (
                  <>
                    {rows.map(r => (
                      <tr key={r.class_name} className="border-b hover:bg-gray-50" data-testid={`dd-classrow-${r.class_name}`}>
                        <td className="px-4 py-2 font-medium text-gray-800">{r.class_name}</td>
                        <td className="px-4 py-2 text-right text-blue-700 font-semibold">{r.percentage != null ? `${r.percentage}%` : '-'}</td>
                        <td className="px-4 py-2 text-right">{r.commit.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right font-medium">{r.drawdown.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-green-600">{r.received.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-red-500">{(r.drawdown - r.received).toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2 text-right">{rows.reduce((s, r) => s + r.commit, 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">{rows.reduce((s, r) => s + r.drawdown, 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-green-700">{rows.reduce((s, r) => s + r.received, 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-red-600">{rows.reduce((s, r) => s + (r.drawdown - r.received), 0).toLocaleString()}</td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="flex justify-between text-sm mb-2"><span className="text-gray-600">Collection Progress</span><span className="font-semibold">{pctReceived}%</span></div>
        <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, pctReceived)}%` }} /></div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${tab.id}`}>
                <Icon size={14} className="inline mr-1.5" />{tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ===== Summary Tab ===== */}
      {activeTab === 'summary' && (
        <div className="bg-white border rounded-lg p-6" data-testid="tab-content-summary">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Drawdown Summary</h3>
            {canEdit && !editingSummary && <button onClick={() => setEditingSummary(true)} className="text-sm text-blue-600 hover:text-blue-700"><Edit size={14} className="inline mr-1" />Edit</button>}
          </div>
          {editingSummary ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-gray-700">Drawdown Name</label><input type="text" value={summaryForm.drawdown_name} onChange={e => setSummaryForm(p => ({ ...p, drawdown_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" /></div>
                <div><label className="text-sm font-medium text-gray-700">% Called</label><input type="number" step="0.01" value={summaryForm.percentage_called} onChange={e => setSummaryForm(p => ({ ...p, percentage_called: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" /></div>
                <div><label className="text-sm font-medium text-gray-700">Notice Date</label><input type="date" value={summaryForm.notice_date} onChange={e => setSummaryForm(p => ({ ...p, notice_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" /></div>
                <div><label className="text-sm font-medium text-gray-700">Due Date</label><input type="date" value={summaryForm.due_date} onChange={e => setSummaryForm(p => ({ ...p, due_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" /></div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Receipt Remarks Template</label>
                <input type="text" value={summaryForm.receipt_remarks_template || ''}
                  onChange={e => setSummaryForm(p => ({ ...p, receipt_remarks_template: e.target.value }))}
                  placeholder="{{drawdown_name}}"
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1 font-mono"
                  data-testid="edit-receipt-remarks-template" />
                <p className="text-xs text-gray-500 mt-1">Auto-fills the Remarks on every receipt's Drawdown Received transaction. Use any of the placeholders below.</p>
                <ReceiptRemarksHint />
              </div>

              {/* Password Hint to Investor — independently editable so admins can
                  tweak the wording without re-running notice generation. Ships
                  as `{{password_hint}}` in email + WhatsApp templates. */}
              <div data-testid="dd-summary-password-hint-block">
                <label className="text-sm font-medium text-gray-700">
                  Password Hint to Investor
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    · ships as <code className="bg-gray-100 px-1 rounded">{`{{password_hint}}`}</code> in your email + WhatsApp templates
                  </span>
                </label>
                <textarea
                  value={summaryForm.password_hint || ''}
                  onChange={e => setSummaryForm(p => ({ ...p, password_hint: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. Your password is the first 4 letters of your name (lowercase) followed by your date of birth in DDMM format."
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-blue-500 leading-relaxed"
                  data-testid="dd-summary-password-hint-input"
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                  <span>Investor-facing wording — keep it human. Avoid revealing the actual password directly here.</span>
                  <span className="text-gray-400">{(summaryForm.password_hint || '').length}/500</span>
                </p>
              </div>

              {/* Long-form email variant — separately editable so WhatsApp can
                  stay concise while emails carry full step-by-step guidance.
                  Ships as `{{password_hint_email}}` in email templates only. */}
              <div data-testid="dd-summary-password-hint-email-block">
                <label className="text-sm font-medium text-gray-700">
                  Password Hint to Investor for Email
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    · ships as <code className="bg-gray-100 px-1 rounded">{`{{password_hint_email}}`}</code> in your email templates (long form)
                  </span>
                </label>
                <textarea
                  value={summaryForm.password_hint_email || ''}
                  onChange={e => setSummaryForm(p => ({ ...p, password_hint_email: e.target.value }))}
                  rows={6}
                  maxLength={4000}
                  placeholder="Longer, richer instructions for the email body — e.g. two-line derivation rule + a worked example + a &quot;reply if you need help&quot; sentence."
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-blue-500 leading-relaxed"
                  data-testid="dd-summary-password-hint-email-input"
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                  <span>Use this variant when you want richer copy in the email than the short hint above allows.</span>
                  <span className="text-gray-400">{(summaryForm.password_hint_email || '').length}/4000</span>
                </p>
              </div>
              {summaryForm.custom_fields && summaryForm.custom_fields.length > 0 && (
                <div className="pt-3 mt-3 border-t" data-testid="dd-custom-fields-edit">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Custom Fields</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {summaryForm.custom_fields.map((cf, idx) => {
                      const updateValue = (val) => setSummaryForm(p => {
                        const arr = [...(p.custom_fields || [])];
                        arr[idx] = { ...arr[idx], field_value: val };
                        return { ...p, custom_fields: arr };
                      });
                      const inputType = cf.field_type === 'number' ? 'number'
                        : cf.field_type === 'date' ? 'date' : 'text';
                      return (
                        <div key={cf.field_key || idx}>
                          <label className="text-sm font-medium text-gray-700">{cf.field_name || cf.field_key}</label>
                          {cf.field_type === 'longtext' ? (
                            <textarea
                              value={cf.field_value ?? ''}
                              onChange={e => updateValue(e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                              rows={3}
                              data-testid={`edit-cf-${cf.field_key || idx}`}
                            />
                          ) : cf.field_type === 'dropdown' ? (
                            <select
                              value={cf.field_value ?? ''}
                              onChange={e => updateValue(e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                              data-testid={`edit-cf-${cf.field_key || idx}`}>
                              <option value="">-- Select --</option>
                              {(cf.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          ) : cf.field_type === 'radio' ? (
                            <div className="flex flex-wrap gap-3 mt-1" data-testid={`edit-cf-${cf.field_key || idx}`}>
                              {(cf.options || []).map(opt => (
                                <label key={opt} className="inline-flex items-center text-sm">
                                  <input
                                    type="radio"
                                    name={`cf-radio-${idx}`}
                                    value={opt}
                                    checked={cf.field_value === opt}
                                    onChange={e => updateValue(e.target.value)}
                                    className="mr-1.5"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <input
                              type={inputType}
                              value={cf.field_value ?? ''}
                              onChange={e => updateValue(inputType === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                              data-testid={`edit-cf-${cf.field_key || idx}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditingSummary(false)} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
                <button onClick={handleUpdateSummary} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-summary-btn"><Save size={14} className="mr-1 inline" />Save</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8 text-sm">
              <div><span className="text-gray-500 block">Fund</span><span className="font-medium">{drawdown.fund_name}</span></div>
              <div><span className="text-gray-500 block">Currency</span><span className="font-medium">{drawdown.currency || '-'}</span></div>
              <div><span className="text-gray-500 block">Drawdown Number</span><span className="font-medium font-mono">{drawdown.drawdown_number}</span></div>
              <div><span className="text-gray-500 block">% Called</span><span className="font-medium text-blue-600">{drawdown.percentage_called}%</span></div>
              <div><span className="text-gray-500 block">Fund Commitment</span><span className="font-medium">{(drawdown.fund_commitment || 0).toLocaleString()}</span></div>
              <div><span className="text-gray-500 block">Total Drawdown</span><span className="font-medium">{(drawdown.total_drawdown_amount || 0).toLocaleString()}</span></div>
              <div><span className="text-gray-500 block">Notice Date</span><span className="font-medium">{fmtDate(drawdown.notice_date) || '-'}</span></div>
              <div><span className="text-gray-500 block">Due Date</span><span className="font-medium">{fmtDate(drawdown.due_date) || '-'}</span></div>
              <div><span className="text-gray-500 block">Status</span><span className={`font-medium capitalize`}>{statusLabel(drawdown.status)}</span></div>
              <div className="md:col-span-3"><span className="text-gray-500 block">Receipt Remarks Template</span><span className="font-mono text-xs text-gray-700" data-testid="dd-receipt-remarks-display">{drawdown.receipt_remarks_template || '{{drawdown_name}} (default)'}</span></div>
              <div className="md:col-span-3" data-testid="dd-summary-password-hint-display">
                <span className="text-gray-500 block">
                  Password Hint to Investor
                  <span className="ml-1 text-[10px] text-gray-400 font-normal">
                    {`{{password_hint}}`} placeholder
                  </span>
                </span>
                <span className="font-medium text-gray-800 break-words whitespace-pre-wrap">
                  {drawdown.password_hint || <em className="text-gray-400 font-normal">— not set —</em>}
                </span>
              </div>
              <div className="md:col-span-3" data-testid="dd-summary-password-hint-email-display">
                <span className="text-gray-500 block">
                  Password Hint to Investor for Email
                  <span className="ml-1 text-[10px] text-gray-400 font-normal">
                    {`{{password_hint_email}}`} placeholder
                  </span>
                </span>
                <span className="font-medium text-gray-800 break-words whitespace-pre-wrap">
                  {drawdown.password_hint_email || <em className="text-gray-400 font-normal">— not set —</em>}
                </span>
              </div>
              {drawdown.custom_fields && drawdown.custom_fields.length > 0 && (
                <div className="md:col-span-3 pt-3 mt-3 border-t" data-testid="dd-custom-fields-summary">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Custom Fields</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
                    {drawdown.custom_fields.map((cf, idx) => (
                      <div key={cf.field_key || idx} data-testid={`dd-custom-field-${cf.field_key}`}>
                        <span className="text-gray-500 block">{cf.field_name || cf.field_key}</span>
                        <span className="font-medium text-gray-800 break-words">
                          {cf.field_value === '' || cf.field_value == null ? '-' : String(cf.field_value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== Investors Tab ===== */}
      {activeTab === 'investors' && (
        <div data-testid="tab-content-investors">
          {/* Pending Receipt Approvals Banner */}
          {(() => {
            const pendingItems = investors.flatMap(inv => (inv.receipts || [])
              .filter(r => (r.approval_status || 'approved') === 'pending_approval')
              .map(r => ({ inv, r })));
            if (pendingItems.length === 0) return null;
            const totalAmt = pendingItems.reduce((s, x) => s + (x.r.amount || 0), 0);
            return (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="pending-receipts-banner">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <AlertTriangle size={16} />
                  <span><strong>{pendingItems.length}</strong> receipt{pendingItems.length > 1 ? 's' : ''} pending approval ({totalAmt.toLocaleString()}). Expand the investor row to Approve or Reject — or approve them all in one click.</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const exp = {};
                    pendingItems.forEach(x => { exp[x.inv.id] = true; });
                    setExpandedRows(prev => ({ ...prev, ...exp }));
                  }} className="text-xs px-2 py-1 border border-amber-400 text-amber-700 rounded hover:bg-amber-100" data-testid="expand-all-pending-btn">
                    Show all pending
                  </button>
                  {(isAdmin || isManager) && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Approve every pending receipt across this drawdown (${pendingItems.length})? Receipts you submitted yourself will be skipped if you're a Fund Manager.`)) return;
                        setActionLoading(true);
                        try {
                          const res = await drawdownAPI.bulkApproveDrawdownReceipts(id);
                          const { approved, skipped_self_made, errors } = res.data || {};
                          const parts = [`Approved ${approved || 0}`];
                          if (skipped_self_made) parts.push(`${skipped_self_made} skipped (cannot self-approve)`);
                          if (errors && errors.length) parts.push(`${errors.length} error(s)`);
                          alert(parts.join(' · '));
                          await loadData();
                        } catch (err) { alert(err.response?.data?.detail || 'Bulk approve failed'); }
                        finally { setActionLoading(false); }
                      }}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                      data-testid="dd-bulk-approve-btn">
                      <CheckCircle size={14} />
                      {actionLoading ? 'Approving…' : `Approve All (${pendingItems.length})`}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          {/* Search Bar + Add Investor */}
          <div className="mb-3 flex items-center gap-2" data-testid="investor-search-wrapper">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                value={investorSearch}
                onChange={e => setInvestorSearch(e.target.value)}
                placeholder="Search by name, class, PPM, folio..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                data-testid="investor-search-input"
              />
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={openAddInvestorModal}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="dd-add-investor-btn"
                title="Add an investor account to this drawdown (maker-checker)"
              >
                <Plus size={14} /> Add Investor
              </button>
            )}
          </div>
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-3 py-2.5">Account Name</th>
                <th className="px-3 py-2.5">Class</th>
                <th className="px-3 py-2.5">PPM / Folio</th>
                <th className="px-3 py-2.5 text-right">Commitment</th>
                <th className="px-3 py-2.5 text-right">Drawdown Amt</th>
                <th className="px-3 py-2.5 text-right">Received</th>
                <th className="px-3 py-2.5 text-right">Outstanding</th>
                <th className="px-3 py-2.5 text-center">Tranches</th>
                <th className="px-3 py-2.5 text-center">Notice</th>
                <th className="px-3 py-2.5">Maker / Checker</th>
                <th className="px-3 py-2.5 w-28">Action</th>
              </tr></thead>
              <tbody>
                {filteredInvestors.map(inv => {
                  const outstanding = (inv.drawdown_amount || 0) - (inv.received_amount || 0);
                  const receipts = inv.receipts || [];
                  const isExpanded = expandedRows[inv.id];
                  return (
                    <React.Fragment key={inv.id}>
                      <tr className="border-b hover:bg-gray-50" data-testid={`dd-inv-row-${inv.id}`}>
                        <td className="px-3 py-2.5">
                          {receipts.length > 0 && (
                            <button onClick={() => toggleRow(inv.id)} className="text-gray-400 hover:text-gray-600" data-testid={`expand-row-${inv.id}`}>
                              {isExpanded ? '▾' : '▸'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-gray-900">
                          {inv.account_name}
                          {inv.pending_action === 'add' && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700 font-medium" title={`Pending add by ${inv.pending_by_user_email || 'maker'}`} data-testid={`pending-add-badge-${inv.id}`}>
                              Pending Add
                            </span>
                          )}
                          {inv.pending_action === 'delete' && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 font-medium" title={`Pending delete by ${inv.pending_by_user_email || 'maker'}`} data-testid={`pending-delete-badge-${inv.id}`}>
                              Pending Delete
                            </span>
                          )}
                          {inv.auto_added && inv.needs_review && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm('Mark this auto-added row as reviewed? It will keep counting toward the drawdown but the "needs review" flag will be cleared.')) return;
                                try {
                                  await drawdownAPI.markInvestorReviewed(id, inv.id);
                                  await loadData();
                                } catch { alert('Failed to mark reviewed'); }
                              }}
                              className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium cursor-pointer"
                              title="Click to mark this auto-added row as reviewed"
                              data-testid={`auto-added-badge-${inv.id}`}
                            >
                              Auto-added · Mark reviewed
                            </button>
                          )}
                          {inv.transfer_lineage && (
                            <span
                              className={`ml-2 px-1.5 py-0.5 text-[10px] rounded-full font-medium border ${
                                inv.transfer_lineage.direction === 'in'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-orange-50 text-orange-700 border-orange-200'
                              }`}
                              title={
                                inv.transfer_lineage.direction === 'in'
                                  ? `Received via ${inv.transfer_lineage.transfer_reference || 'a transfer'} from ${inv.transfer_lineage.counterparty_name || 'transferor'}${inv.transfer_lineage.counterparty_folio_no ? ' · ' + inv.transfer_lineage.counterparty_folio_no : ''}`
                                  : `Partially transferred out via ${inv.transfer_lineage.transfer_reference || 'a transfer'} to ${inv.transfer_lineage.counterparty_name || 'transferee'}`
                              }
                              data-testid={`transfer-lineage-${inv.transfer_lineage.direction}-${inv.id}`}
                            >
                              ↳ {inv.transfer_lineage.direction === 'in' ? 'from' : 'to'} {inv.transfer_lineage.counterparty_name || 'counterparty'}
                              {inv.transfer_lineage.transfer_reference ? ` · ${inv.transfer_lineage.transfer_reference}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs">{inv.class_name ? <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">{inv.class_name}</span> : <span className="text-gray-400">-</span>}</td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs">{inv.ppm_no || '-'} / {inv.folio_no || '-'}</td>
                        <td className="px-3 py-2.5 text-right">{(inv.commitment_amount || 0).toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right">
                          {editingInvestor === inv.id ? (
                            <div className="flex items-center gap-1 justify-end">
                              <input type="number" value={editInvAmount} onChange={e => setEditInvAmount(e.target.value)} className="w-24 border rounded px-2 py-1 text-sm text-right" autoFocus />
                              <button onClick={() => handleSaveInvestorAmount(inv.id)} className="text-green-600"><Check size={14} /></button>
                              <button onClick={() => setEditingInvestor(null)} className="text-gray-400"><X size={14} /></button>
                            </div>
                          ) : (
                            <span className={`${canEdit ? 'cursor-pointer hover:text-blue-600' : ''}`}
                              onClick={() => { if (canEdit) { setEditingInvestor(inv.id); setEditInvAmount(inv.drawdown_amount); } }}>
                              {(inv.drawdown_amount || 0).toLocaleString()}
                              {inv.drawdown_amount_overridden && <span className="text-amber-500 text-xs ml-1" title="Overridden">*</span>}
                            </span>
                          )}
                        </td>
                        <td className={`px-3 py-2.5 text-right font-medium ${inv.received_amount > 0 ? 'text-green-600' : 'text-gray-400'}`}>{(inv.received_amount || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-right font-medium ${outstanding > 0 ? 'text-red-500' : 'text-green-600'}`}>{outstanding.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="text-xs text-gray-500">{receipts.length}</span>
                          {(() => {
                            const pendingCount = receipts.filter(r => (r.approval_status || 'approved') === 'pending_approval').length;
                            return pendingCount > 0 ? (
                              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`pending-badge-${inv.id}`}>
                                {pendingCount} pending
                              </span>
                            ) : null;
                          })()}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {(() => {
                            const invNotices = notices.filter(n => n.drawdown_investor_id === inv.id && n.reviewed);
                            if (invNotices.length > 0) {
                              return (
                                <button onClick={() => handleDownloadNotice(invNotices[0], invNotices[0].pdf_storage_path ? 'pdf' : 'docx')}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded-full border border-green-200 hover:bg-green-100"
                                  title={`Download: ${invNotices[0].file_name}`} data-testid={`inv-notice-${inv.id}`}>
                                  <CheckCircle size={10} /><Download size={10} />
                                </button>
                              );
                            }
                            return <span className="text-xs text-gray-300">-</span>;
                          })()}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-300 text-center">—</td>
                        <td className="px-3 py-2.5">
                          {(() => {
                            const pa = inv.pending_action;
                            const otherUser = inv.pending_by_user_id && inv.pending_by_user_id !== user?.id;
                            const canApprovePa = pa && (isAdmin || (isManager && otherUser));
                            const canRejectPa = pa && (isAdmin || (isManager && otherUser));
                            if (pa === 'add') {
                              return (
                                <div className="flex items-center gap-1">
                                  {canApprovePa && (
                                    <button onClick={() => handleApproveAddInvestor(inv.id)} className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100" data-testid={`approve-add-${inv.id}`}>Approve</button>
                                  )}
                                  {canRejectPa && (
                                    <button onClick={() => handleRejectInvestorAction(inv.id, 'add')} className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100" data-testid={`reject-add-${inv.id}`}>Reject</button>
                                  )}
                                  {!canApprovePa && !canRejectPa && <span className="text-xs text-gray-400">Awaiting checker</span>}
                                </div>
                              );
                            }
                            if (pa === 'delete') {
                              return (
                                <div className="flex items-center gap-1">
                                  {canApprovePa && (
                                    <button onClick={() => handleApproveDeleteInvestor(inv.id)} className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100" data-testid={`approve-delete-${inv.id}`}>Approve Delete</button>
                                  )}
                                  {canRejectPa && (
                                    <button onClick={() => handleRejectInvestorAction(inv.id, 'delete')} className="px-2 py-1 text-xs bg-gray-50 text-gray-700 border border-gray-200 rounded hover:bg-gray-100" data-testid={`reject-delete-${inv.id}`}>Cancel</button>
                                  )}
                                  {!canApprovePa && !canRejectPa && <span className="text-xs text-gray-400">Awaiting checker</span>}
                                </div>
                              );
                            }
                            return (
                              <div className="flex items-center gap-1">
                                {canEdit && (
                                  <button onClick={() => { setReceiptModal(inv); setReceiptForm({ amount: outstanding > 0 ? outstanding : '', date_of_receipt: '' }); }}
                                    className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 inline-flex items-center gap-1" data-testid={`record-receipt-${inv.id}`}>
                                    <Plus size={12} />Receipt
                                  </button>
                                )}
                                {canEdit && (inv.received_amount || 0) === 0 && (
                                  <button onClick={() => handleSubmitDeleteInvestor(inv.id, inv.account_name, inv.class_name)} className="px-1.5 py-1 text-xs text-red-500 hover:bg-red-50 rounded" title="Request delete (maker-checker)" data-testid={`submit-delete-${inv.id}`}>
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {/* Expanded receipt tranches */}
                      {isExpanded && receipts.map((r, idx) => {
                        const rStatus = r.approval_status || 'approved';
                        const rMaker = r.submitted_by_user_id;
                        const canApproveR = isAdmin || (isManager && user?.id && rMaker && rMaker !== user?.id);
                        const rColor = rStatus === 'approved' ? 'bg-green-100 text-green-700'
                          : rStatus === 'rejected' ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700';
                        return (
                        <tr key={r.id} className="bg-gray-50/80 border-b" data-testid={`receipt-row-${r.id}`}>
                          <td className="px-3 py-2"></td>
                          <td colSpan={2} className="px-3 py-2 text-xs text-gray-500 pl-8">
                            Tranche {idx + 1}
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${rColor}`} data-testid={`receipt-status-${r.id}`}>{rStatus.replace(/_/g,' ')}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(r.date_of_receipt)}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right text-xs font-medium text-green-600">{(r.amount || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-[120px] truncate">
                            {rStatus === 'rejected' && r.rejection_reason
                              ? <span className="text-red-600" title={r.rejection_reason}>Rejected: {r.rejection_reason}</span>
                              : (r.soa_remarks || '-')}
                          </td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-[11px]" data-testid={`tranche-audit-${r.id}`}>
                            {(r.submitted_by || r.approved_by) ? (
                              <div className="flex flex-col gap-0.5">
                                {r.submitted_by && (
                                  <div className="flex items-center gap-1" title={r.submitted_by.email || ''}>
                                    <span className="text-[9px] uppercase tracking-wider text-gray-400 w-12">Maker</span>
                                    <span className="text-gray-700 truncate max-w-[110px]">{r.submitted_by.name}</span>
                                  </div>
                                )}
                                {r.approved_by && (
                                  <div className="flex items-center gap-1" title={r.approved_by.email || ''}>
                                    <span className="text-[9px] uppercase tracking-wider text-green-500 w-12">Checker</span>
                                    <span className="text-gray-700 truncate max-w-[110px]">{r.approved_by.name}</span>
                                  </div>
                                )}
                              </div>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2">
                            {canEdit && (
                              <div className="flex items-center gap-1">
                                {rStatus === 'pending_approval' && canApproveR && (
                                  <>
                                    <button onClick={() => handleApproveReceipt(inv.id, r.id)} disabled={actionLoading}
                                      className="p-1 text-green-600 hover:bg-green-100 rounded" title="Approve receipt"
                                      data-testid={`approve-receipt-${r.id}`}><Check size={12} /></button>
                                    <button onClick={() => setRejectModal({ kind: 'receipt', investorId: inv.id, receiptId: r.id, reason: '' })} disabled={actionLoading}
                                      className="p-1 text-red-600 hover:bg-red-100 rounded" title="Reject receipt"
                                      data-testid={`reject-receipt-${r.id}`}><X size={12} /></button>
                                  </>
                                )}
                                <button onClick={() => { setEditingReceipt({ investorId: inv.id, receipt: r }); setEditReceiptForm({ amount: r.amount, date_of_receipt: r.date_of_receipt, soa_remarks: r.soa_remarks || '' }); }}
                                  className="p-1 text-gray-400 hover:text-blue-600" data-testid={`edit-receipt-${r.id}`}><Edit size={12} /></button>
                                <button onClick={() => handleDeleteReceipt(inv.id, r.id)}
                                  className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-receipt-${r.id}`}><Trash2 size={12} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {investors.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No investors for this drawdown</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Reports Tab ===== */}
      {activeTab === 'reports' && (
        <div data-testid="tab-content-reports">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'pending', label: 'Pending' },
                { id: 'partially_pending', label: 'Partially Pending' },
                { id: 'received', label: 'Received' },
              ].map(f => (
                <button key={f.id} onClick={() => loadReport(f.id)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${reportFilter === f.id ? 'bg-blue-600 text-white' : 'border text-gray-600 hover:bg-gray-50'}`}
                  data-testid={`report-filter-${f.id}`}>{f.label}</button>
              ))}
            </div>
            {report && (
              <div className="flex items-center gap-2">
                <button onClick={() => handleExportReport('excel')} disabled={exporting}
                  className="px-3 py-1.5 text-xs border rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="export-excel-btn">
                  {exporting === 'excel' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Export Excel
                </button>
                <button onClick={() => handleExportReport('pdf')} disabled={exporting}
                  className="px-3 py-1.5 text-xs border rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="export-pdf-btn">
                  {exporting === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Export PDF
                </button>
                <button onClick={async () => {
                  setEmailReportOpen(true);
                  // Lazy-load AMC defaults + team-members so the modal opens
                  // prefilled with placeholders, distribution list, and a
                  // populated dropdown.
                  try {
                    const [{ data: defaults }, { data: teamData }] = await Promise.all([
                      drawdownAPI.getReportEmailDefaults(),
                      drawdownAPI.getTeamMembers(),
                    ]);
                    setTeamMembers(teamData.members || []);
                    setCanEmailExternal(!!teamData.current_user_can_email_external);
                    // Pre-fill recipients + format from the AMC's saved
                    // distribution list. Per product decision: always overwrite
                    // any stale state so admins know exactly who gets the next
                    // send.
                    const defaultTo = (defaults.default_to_members || []).map(m => m.email);
                    const defaultCc = (defaults.default_cc_members || []).map(m => m.email);
                    setEmailReportForm((prev) => ({
                      ...prev,
                      to: defaultTo,
                      cc: defaultCc,
                      format: defaults.default_format || prev.format || 'excel',
                      _defaults: { subject: defaults.subject, body: defaults.body },
                    }));
                  } catch { /* tolerate — modal still works with empty list */ }
                }}
                  className="px-3 py-1.5 text-xs border rounded-lg text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1.5"
                  data-testid="email-report-btn">
                  <Send size={12} /> Email Report
                </button>
                <button
                  onClick={async () => {
                    setWaReportOpen(true);
                    setWaReportLoading(true);
                    setWaReportPreflight(null);
                    setWaReportForm({ template_id: '', recipient_user_ids: [], format: 'pdf' });
                    try {
                      const { data } = await drawdownAPI.whatsappReportPreflight(id);
                      setWaReportPreflight(data);
                      // Auto-select the first send-ready template (has the report-link variable mapped)
                      const firstReady = (data.templates || []).find((t) => t.is_send_ready);
                      setWaReportForm((prev) => ({
                        ...prev,
                        template_id: firstReady ? firstReady.id : '',
                      }));
                    } catch (e) {
                      setWaReportPreflight({
                        eligible: false,
                        reason: 'error',
                        message: e.response?.data?.detail || 'Failed to load WhatsApp Report preflight.',
                        templates: [],
                        team_members: [],
                      });
                    } finally {
                      setWaReportLoading(false);
                    }
                  }}
                  className="px-3 py-1.5 text-xs border rounded-lg text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5"
                  data-testid="whatsapp-report-btn">
                  <MessageCircle size={12} /> WhatsApp Report
                </button>
              </div>
            )}
          </div>

          {/* Configure Report Columns */}
          <div className="mb-3 bg-white border rounded-lg" data-testid="report-extra-fields">
            <button
              onClick={() => setShowReportFieldPicker(prev => !prev)}
              className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-gray-50 transition-colors"
              data-testid="toggle-report-columns-btn"
            >
              <div className="flex items-center gap-2">
                <Columns size={14} className="text-gray-500" />
                <span className="font-medium text-gray-700">Configure Report Columns</span>
                {reportExtraFields.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">{reportExtraFields.length}</span>
                )}
              </div>
              <span className="text-gray-400 text-xs">{showReportFieldPicker ? 'Hide' : 'Show'}</span>
            </button>
            {showReportFieldPicker && (
              <div className="px-4 pb-3 border-t" data-testid="report-column-picker">
                <div className="flex items-center justify-between mt-2.5 mb-2">
                  <p className="text-xs text-gray-500">Select additional columns from Investor Account data</p>
                  {reportExtraFields.length > 0 && (
                    <button onClick={() => setReportExtraFields([])} className="text-xs text-red-500 hover:text-red-600" data-testid="clear-extra-fields-btn">Clear All</button>
                  )}
                </div>
                {Object.entries(REPORT_FIELD_SECTIONS).map(([sectionKey, section]) => {
                  const sectionFields = section.fields.map(f => f.key);
                  const allSelected = sectionFields.every(k => reportExtraFields.includes(k));
                  const someSelected = sectionFields.some(k => reportExtraFields.includes(k));
                  return (
                    <div key={sectionKey} className="mb-3" data-testid={`report-section-${sectionKey}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleReportSection(sectionKey)}
                          className="rounded border-gray-300 text-blue-600 w-3.5 h-3.5"
                          data-testid={`report-section-toggle-${sectionKey}`}
                        />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{section.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pl-5">
                        {section.fields.map(f => (
                          <button
                            key={f.key}
                            onClick={() => toggleReportField(f.key)}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${
                              reportExtraFields.includes(f.key)
                                ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                            data-testid={`extra-field-${f.key}`}
                          >{f.label}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {report && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-500">Investors</p><p className="text-lg font-bold">{report.summary.total_investors}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-500">Total Commitment</p><p className="text-lg font-bold">{report.summary.total_commitment.toLocaleString()}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-500">Total Drawdown</p><p className="text-lg font-bold">{report.summary.total_drawdown.toLocaleString()}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-500">Total Received</p><p className="text-lg font-bold text-green-600">{report.summary.total_received.toLocaleString()}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-500">Outstanding</p><p className="text-lg font-bold text-red-500">{report.summary.total_outstanding.toLocaleString()}</p></div>
              </div>
              <div className="bg-white border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                    <th className="px-3 py-2.5">Account</th><th className="px-3 py-2.5">Class</th><th className="px-3 py-2.5">Folio</th>
                    {ALL_REPORT_FIELDS.filter(f => reportExtraFields.includes(f.key)).map(f => (
                      <th key={f.key} className="px-3 py-2.5">{f.label}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right">Commitment</th><th className="px-3 py-2.5 text-right">Drawdown</th>
                    <th className="px-3 py-2.5 text-right">Received</th><th className="px-3 py-2.5 text-right">Outstanding</th><th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Remarks</th>
                  </tr></thead>
                  <tbody>
                    {report.investors.map(inv => {
                      const received = inv.received_amount || 0;
                      const drawdown = inv.drawdown_amount || 0;
                      const out = drawdown - received;
                      // Server emits an `effective_status` that already
                      // respects manual overrides. Fallback computation is
                      // kept defensively for older payloads.
                      let status = inv.effective_status;
                      if (!status) {
                        if (drawdown > 0 && received >= drawdown) status = 'Received';
                        else if (received > 0 && received < drawdown) status = 'Partially Pending';
                        else status = 'Pending';
                      }
                      const statusClass = status === 'Received'
                        ? 'bg-green-100 text-green-700'
                        : status === 'Partially Pending'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-amber-100 text-amber-700';
                      const isOverridden = !!inv.status_override;
                      const handleSet = async (newStatus) => {
                        try {
                          await drawdownAPI.setStatusOverride(id, inv.id, newStatus);
                          await loadReport(reportFilter);
                        } catch (e) {
                          alert(e.response?.data?.detail || 'Failed to update status');
                        }
                      };
                      return (
                        <tr key={inv.id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2.5 font-medium">{inv.account_name}</td>
                          <td className="px-3 py-2.5 text-xs">{inv.class_name || '-'}</td>
                          <td className="px-3 py-2.5 text-gray-600">{inv.folio_no || '-'}</td>
                          {ALL_REPORT_FIELDS.filter(f => reportExtraFields.includes(f.key)).map(f => (
                            <td key={f.key} className="px-3 py-2.5 text-gray-600 text-xs max-w-[180px] truncate" title={inv[f.key] || '-'}>
                              {inv[f.key] || '-'}
                            </td>
                          ))}
                          <td className="px-3 py-2.5 text-right">{(inv.commitment_amount || 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right">{drawdown.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right text-green-600">{received.toLocaleString()}</td>
                          <td className={`px-3 py-2.5 text-right font-medium ${out > 0 ? 'text-red-500' : 'text-green-600'}`}>{out.toLocaleString()}</td>
                          <td className="px-3 py-2.5">
                            <div className="inline-flex items-center gap-1.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`} data-testid={`report-status-${inv.id}`}>
                                {status}
                                {isOverridden && <span className="ml-1 opacity-70" title={`Manually overridden by ${inv.status_override_by || 'admin'}`}>•</span>}
                              </span>
                              {canEdit && (
                                <select
                                  value={isOverridden ? inv.status_override : ''}
                                  onChange={(e) => handleSet(e.target.value || null)}
                                  className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600 hover:border-gray-400"
                                  data-testid={`report-status-override-${inv.id}`}
                                  title="Manually override the status — set or reset to auto"
                                >
                                  <option value="">Auto</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Partially Pending">Partially Pending</option>
                                  <option value="Received">Received</option>
                                </select>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            {(status === 'Pending' || status === 'Partially Pending') ? (
                              canEdit ? (
                                <input
                                  type="text"
                                  defaultValue={inv.remarks || ''}
                                  placeholder="Add follow-up note..."
                                  className="w-full min-w-[180px] text-xs border border-gray-200 rounded px-2 py-1 focus:border-blue-400 focus:outline-none"
                                  data-testid={`report-remarks-${inv.id}`}
                                  title={inv.remarks_by ? `Last updated by ${inv.remarks_by}` : 'Capture any follow-up information'}
                                  onBlur={async (e) => {
                                    const newVal = e.target.value.trim();
                                    if ((newVal || '') === (inv.remarks || '')) return;  // no-op
                                    try {
                                      await drawdownAPI.setRemarks(id, inv.id, newVal);
                                      await loadReport(reportFilter);
                                    } catch (err) {
                                      alert(err.response?.data?.detail || 'Failed to save remarks');
                                    }
                                  }}
                                />
                              ) : (
                                <span className="text-xs text-gray-600 line-clamp-2">{inv.remarks || '-'}</span>
                              )
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {emailReportOpen && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="email-report-modal">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="px-5 py-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Email Drawdown Report</h3>
                  <button onClick={() => setEmailReportOpen(false)} className="text-gray-400 hover:text-gray-700">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                    Sending the <strong>{reportFilter === 'all' ? 'All Investors' : reportFilter === 'partially_pending' ? 'Partially Pending Only' : reportFilter === 'pending' ? 'Pending Only' : 'Received Only'}</strong> view, in <strong>{emailReportForm.format === 'excel' ? 'Excel' : 'PDF'}</strong> format. Filter & columns match what you've configured above.
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To <span className="text-red-500">*</span></label>
                    <RecipientPicker
                      field="to"
                      selected={emailReportForm.to}
                      onChange={(arr) => setEmailReportForm({ ...emailReportForm, to: arr })}
                      teamMembers={teamMembers}
                      canEmailExternal={canEmailExternal}
                      extInput={emailReportForm.extToInput}
                      onExtInputChange={(v) => setEmailReportForm({ ...emailReportForm, extToInput: v })}
                      commitExt={(email) => {
                        const e = (email || '').trim();
                        if (!e || emailReportForm.to.includes(e)) return;
                        setEmailReportForm({ ...emailReportForm, to: [...emailReportForm.to, e], extToInput: '' });
                      }}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Pick internal team members from the dropdown.
                      {canEmailExternal
                        ? ' You can also type any external email and press Enter.'
                        : ' Ask an admin to enable "Email External Recipients" to send to non-internal addresses.'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">CC (optional)</label>
                    <RecipientPicker
                      field="cc"
                      selected={emailReportForm.cc}
                      onChange={(arr) => setEmailReportForm({ ...emailReportForm, cc: arr })}
                      teamMembers={teamMembers}
                      canEmailExternal={canEmailExternal}
                      extInput={emailReportForm.extCcInput}
                      onExtInputChange={(v) => setEmailReportForm({ ...emailReportForm, extCcInput: v })}
                      commitExt={(email) => {
                        const e = (email || '').trim();
                        if (!e || emailReportForm.cc.includes(e)) return;
                        setEmailReportForm({ ...emailReportForm, cc: [...emailReportForm.cc, e], extCcInput: '' });
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Format</label>
                      <select
                        value={emailReportForm.format}
                        onChange={(e) => setEmailReportForm({ ...emailReportForm, format: e.target.value })}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                        data-testid="email-report-format"
                      >
                        <option value="excel">Excel (.xlsx)</option>
                        <option value="pdf">PDF</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Subject (optional)</label>
                    <input
                      type="text"
                      value={emailReportForm.subject}
                      onChange={(e) => setEmailReportForm({ ...emailReportForm, subject: e.target.value })}
                      placeholder={emailReportForm._defaults?.subject || 'Leave blank for default'}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="email-report-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Message (optional)</label>
                    <textarea
                      rows={3}
                      value={emailReportForm.message}
                      onChange={(e) => setEmailReportForm({ ...emailReportForm, message: e.target.value })}
                      placeholder={emailReportForm._defaults?.body || 'Add a short note for the team...'}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="email-report-message"
                    />
                  </div>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-xl flex justify-end gap-2">
                  <button
                    onClick={() => setEmailReportOpen(false)}
                    disabled={emailReportSending}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={emailReportSending || emailReportForm.to.length === 0}
                    onClick={async () => {
                      const recipients = emailReportForm.to;
                      const cc = emailReportForm.cc;
                      if (!recipients.length) return;
                      setEmailReportSending(true);
                      try {
                        const res = await drawdownAPI.emailReport(id, {
                          recipients, cc,
                          format: emailReportForm.format,
                          filter: reportFilter,
                          extra_fields: reportExtraFields.join(','),
                          subject: emailReportForm.subject.trim() || undefined,
                          message: emailReportForm.message.trim() || undefined,
                        });
                        alert(`Sent to ${res.data.recipients.length} recipient(s).`);
                        setEmailReportOpen(false);
                        setEmailReportForm({ to: [], cc: [], format: 'excel', subject: '', message: '', extToInput: '', extCcInput: '' });
                      } catch (e) {
                        alert(e.response?.data?.detail || 'Failed to send report');
                      } finally {
                        setEmailReportSending(false);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="email-report-send-btn"
                  >
                    {emailReportSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Send Report
                  </button>
                </div>
              </div>
            </div>
          )}

          {waReportOpen && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="whatsapp-report-modal">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="px-5 py-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                    <MessageCircle size={18} className="text-emerald-600" />
                    WhatsApp Drawdown Report
                  </h3>
                  <button onClick={() => setWaReportOpen(false)} className="text-gray-400 hover:text-gray-700">
                    <X size={18} />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  {waReportLoading && (
                    <div className="flex items-center justify-center py-10 text-gray-500">
                      <Loader2 size={22} className="animate-spin mr-2" /> Loading…
                    </div>
                  )}

                  {!waReportLoading && waReportPreflight && (
                    <>
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-xs text-emerald-800">
                        Sending the <strong>{reportFilter === 'all' ? 'All Investors' : reportFilter === 'partially_pending' ? 'Partially Pending Only' : reportFilter === 'pending' ? 'Pending Only' : 'Received Only'}</strong> view as a temporary <strong>{waReportForm.format === 'pdf' ? 'PDF' : 'Excel'}</strong> download link over WhatsApp. Link expires in 24 hours.
                      </div>

                      {!waReportPreflight.eligible && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="whatsapp-report-preflight-error">
                          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                          <div>
                            {waReportPreflight.message}
                            {waReportPreflight.reason === 'config_not_tested' && (
                              <div className="mt-1">
                                Open <span className="font-semibold">Settings → WhatsApp Configuration</span> and run <span className="font-semibold">Test Connection</span> before sending.
                              </div>
                            )}
                            {waReportPreflight.reason === 'no_active_config' && (
                              <div className="mt-1">
                                Ask your AMC Admin to activate WhatsApp under <span className="font-semibold">Settings → WhatsApp Configuration</span>.
                              </div>
                            )}
                            {waReportPreflight.reason === 'no_report_templates' && (
                              <div className="mt-1">
                                Go to <span className="font-semibold">WhatsApp Templates</span> and create/publish a template under the <span className="font-semibold">Drawdown Report</span> category.
                              </div>
                            )}
                            {waReportPreflight.reason === 'no_report_link_templates' && (
                              <div className="mt-1">
                                Edit one of your Drawdown Report templates and map one variable to the <span className="font-semibold">Drawdown Report Short Link</span> merge field so recipients can click the report.
                              </div>
                            )}
                            {waReportPreflight.reason === 'no_recipients_with_mobile' && (
                              <div className="mt-1">
                                Add <span className="font-semibold">Country Code + Mobile</span> to internal users in <span className="font-semibold">User Management</span>.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Report Format <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setWaReportForm({ ...waReportForm, format: 'pdf' })}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg inline-flex items-center justify-center gap-1.5 ${
                              waReportForm.format === 'pdf'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-800 font-medium'
                                : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                            data-testid="whatsapp-report-format-pdf"
                          >
                            <FileText size={13} /> PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => setWaReportForm({ ...waReportForm, format: 'excel' })}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg inline-flex items-center justify-center gap-1.5 ${
                              waReportForm.format === 'excel'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-800 font-medium'
                                : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                            data-testid="whatsapp-report-format-excel"
                          >
                            <FileSpreadsheet size={13} /> Excel
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Template <span className="text-red-500">*</span></label>
                        <select
                          value={waReportForm.template_id}
                          onChange={(e) => setWaReportForm({ ...waReportForm, template_id: e.target.value })}
                          disabled={!waReportPreflight.templates?.length}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400"
                          data-testid="whatsapp-report-template-select"
                        >
                          <option value="">— Select a Drawdown Report template —</option>
                          {(waReportPreflight.templates || []).map((t) => {
                            const disabled = !t.is_send_ready;
                            const suffix = t.not_ready_reason
                              ? ` (${t.not_ready_reason.length > 45 ? t.not_ready_reason.slice(0, 45) + '…' : t.not_ready_reason})`
                              : '';
                            return (
                              <option key={t.id} value={t.id} disabled={disabled}>
                                {t.name} [{t.language_code}]{suffix}
                              </option>
                            );
                          })}
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Only templates in the <span className="font-semibold">Drawdown Report</span> category with a variable mapped to <span className="font-mono">{'{{drawdown_report_link}}'}</span> can be selected.
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-gray-700">Recipients (internal team) <span className="text-red-500">*</span></label>
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = (waReportPreflight.team_members || [])
                                .filter((m) => m.has_mobile)
                                .map((m) => m.user_id);
                              const same = allIds.length === waReportForm.recipient_user_ids.length
                                && allIds.every((x) => waReportForm.recipient_user_ids.includes(x));
                              setWaReportForm({
                                ...waReportForm,
                                recipient_user_ids: same ? [] : allIds,
                              });
                            }}
                            className="text-[11px] text-blue-600 hover:underline"
                            data-testid="whatsapp-report-toggle-all"
                          >
                            {waReportForm.recipient_user_ids.length === (waReportPreflight.team_members || []).filter((m) => m.has_mobile).length && waReportForm.recipient_user_ids.length > 0
                              ? 'Clear all' : 'Select all eligible'}
                          </button>
                        </div>
                        <div className="border border-gray-200 rounded-lg divide-y max-h-56 overflow-y-auto">
                          {(waReportPreflight.team_members || []).length === 0 && (
                            <div className="p-3 text-xs text-gray-500 text-center">No team members found.</div>
                          )}
                          {(waReportPreflight.team_members || []).map((m) => (
                            <label
                              key={m.user_id}
                              className={`flex items-center gap-2 px-3 py-2 text-sm ${m.has_mobile ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                              data-testid={`whatsapp-report-recipient-${m.user_id}`}
                            >
                              <input
                                type="checkbox"
                                disabled={!m.has_mobile}
                                checked={waReportForm.recipient_user_ids.includes(m.user_id)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setWaReportForm((prev) => ({
                                    ...prev,
                                    recipient_user_ids: checked
                                      ? [...prev.recipient_user_ids, m.user_id]
                                      : prev.recipient_user_ids.filter((x) => x !== m.user_id),
                                  }));
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-gray-900 truncate">{m.name || m.email}</div>
                                <div className="text-[11px] text-gray-500 truncate">
                                  {m.has_mobile
                                    ? `${m.mobile_country_code} ${m.mobile}`
                                    : 'No mobile number — add via User Management'}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-xl flex justify-end gap-2">
                  <button
                    onClick={() => setWaReportOpen(false)}
                    disabled={waReportSending}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={
                      waReportSending || waReportLoading ||
                      !waReportPreflight?.eligible ||
                      !waReportForm.template_id ||
                      waReportForm.recipient_user_ids.length === 0
                    }
                    onClick={async () => {
                      setWaReportSending(true);
                      try {
                        const { data } = await drawdownAPI.whatsappReportSend(id, {
                          template_id: waReportForm.template_id,
                          recipient_user_ids: waReportForm.recipient_user_ids,
                          format: waReportForm.format,
                          filter: reportFilter,
                          extra_fields: reportExtraFields.join(','),
                        });
                        const sent = data.sent_count ?? 0;
                        const failed = data.failed_count ?? 0;
                        const skippedCount = (data.skipped || []).length;
                        const errLines = (data.delivery || [])
                          .filter((d) => d.status === 'failed')
                          .map((d) => `• ${d.name || d.phone}: ${d.error || 'unknown error'}`)
                          .join('\n');
                        let msg = `WhatsApp Report:\n✅ Sent: ${sent}\n❌ Failed: ${failed}`;
                        if (skippedCount) msg += `\n⏭️ Skipped: ${skippedCount} (no mobile)`;
                        if (errLines) msg += `\n\nFailures:\n${errLines}`;
                        alert(msg);
                        if (sent > 0 && failed === 0) setWaReportOpen(false);
                      } catch (e) {
                        alert(e.response?.data?.detail || 'Failed to send WhatsApp Report');
                      } finally {
                        setWaReportSending(false);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="whatsapp-report-send-btn"
                  >
                    {waReportSending ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                    Send WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== Documents Tab ===== */}
      {activeTab === 'documents' && (
        <div data-testid="tab-content-documents">
          {/* Sub-tabs: Generate / Generated Notices */}
          <div className="flex items-center gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
            <button onClick={() => setDocView('generate')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${docView === 'generate' ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="doc-view-generate">Generate Notices</button>
            <button onClick={() => setDocView('notices')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${docView === 'notices' ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="doc-view-notices">
              Generated Notices {notices.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{notices.length}</span>}
            </button>
          </div>

          {/* Generate View */}
          {docView === 'generate' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Template + Options */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Select Template</h3>
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-6 text-gray-400"><Loader2 size={18} className="animate-spin mr-2" /> Loading...</div>
                  ) : fundTemplates.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">No templates found. Upload in Fund &rarr; Document Templates.</p>
                  ) : (
                    <div className="space-y-2">
                      {fundTemplates.map(tmpl => (
                        <button key={tmpl.id}
                          onClick={() => { setSelectedTemplate(tmpl); setSelectedInvestorIds([]); }}
                          className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${selectedTemplate?.id === tmpl.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                          data-testid={`doc-tmpl-${tmpl.id}`}>
                          <FileText size={18} className={tmpl.file_type === 'pdf' ? 'text-red-500' : 'text-blue-500'} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{tmpl.name}</p>
                            <p className="text-xs text-gray-400">{tmpl.file_type.toUpperCase()}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Options panel */}
                {selectedTemplate && (
                  <div className="bg-white border rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-semibold text-gray-800">Options</h3>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Date Format</label>
                      <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
                        className="w-full text-sm border rounded-lg px-2.5 py-2" data-testid="date-format-select">
                        <option value="dd mmmm yyyy">25 January 2026</option>
                        <option value="dd-mm-yyyy">25-01-2026</option>
                        <option value="mm-dd-yyyy">01-25-2026</option>
                        <option value="dd/mm/yyyy">25/01/2026</option>
                        <option value="mm/dd/yyyy">01/25/2026</option>
                        <option value="yyyy-mm-dd">2026-01-25</option>
                        <option value="mmmm dd, yyyy">January 25, 2026</option>
                        <option value="dd-mmmm-yyyy">25-January-2026</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Number Format</label>
                      <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}
                        className="w-full text-sm border rounded-lg px-2.5 py-2" data-testid="number-format-select">
                        <option value="western">Western (1,000,000)</option>
                        <option value="indian">Indian (10,00,000)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        File Naming Pattern <span className="text-gray-400">(optional)</span>
                      </label>
                      <input type="text" value={fileNamingPattern} onChange={e => setFileNamingPattern(e.target.value)}
                        placeholder="e.g. Drawdown Notice - {{ppm_no}} - {{account_name}}"
                        className="w-full text-sm border rounded-lg px-2.5 py-2" data-testid="file-naming-input" />
                      <p className="text-xs text-gray-400 mt-1">Use {'{{field_key}}'} tags. Leave blank for default naming.</p>
                    </div>

                    {/* Consolidate-per-account: only meaningful for class-wise drawdowns */}
                    {drawdown.class_percentages && drawdown.class_percentages.length > 0 && (
                      <div className="border-t pt-3 mt-3">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consolidateByAccount}
                            onChange={e => setConsolidateByAccount(e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded mt-0.5"
                            data-testid="dd-notices-consolidate-account"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            <span className="font-medium">Generate one notice per account</span>
                            <span className="text-gray-500 block text-xs mt-0.5">
                              For accounts holding multiple classes, render a single notice instead of one per class. The template should use
                              <code className="mx-1 px-1 bg-gray-100 rounded">{'{{account_class_called_table}}'}</code>
                              (or per-class scalars) to surface every class&apos;s call.
                            </span>
                          </span>
                        </label>
                      </div>
                    )}

                    {/* ====== PDF Password Protection ====== */}
                    <div className="border-t pt-3 mt-3">
                      <label className="text-xs font-semibold text-gray-700 block mb-2 flex items-center gap-1.5">
                        <Shield size={13} className="text-amber-600" /> PDF Password Protection
                      </label>
                      <select
                        value={passwordMode}
                        onChange={e => setPasswordMode(e.target.value)}
                        className="w-full text-sm border rounded-lg px-2.5 py-2"
                        data-testid="password-mode-select"
                      >
                        <option value="none">None - Generate unprotected PDFs only</option>
                        <option value="single_field">Single Field (e.g. PAN, DOB)</option>
                        <option value="combination">Combination DSL (e.g. lower(first(name,4))+ddmm(dob))</option>
                        <option value="csv">CSV Upload (Investor Ref &rarr; Password)</option>
                      </select>

                      {passwordMode === 'single_field' && (
                        <div className="mt-2" data-testid="password-single-field-block">
                          <label className="text-xs text-gray-500 block mb-1">Password Field</label>
                          <select
                            value={passwordField}
                            onChange={e => setPasswordField(e.target.value)}
                            className="w-full text-sm border rounded-lg px-2.5 py-2"
                            data-testid="password-field-select"
                          >
                            <option value="pan">PAN</option>
                            <option value="dob">Date of Birth (DDMM)</option>
                            <option value="dob_full">Date of Birth (DDMMYYYY)</option>
                            <option value="folio_no">Folio No.</option>
                            <option value="ppm_no">PPM No.</option>
                            <option value="mobile">Mobile (last 4 digits)</option>
                          </select>
                        </div>
                      )}

                      {passwordMode === 'combination' && (
                        <div className="mt-2" data-testid="password-combination-block">
                          <label className="text-xs text-gray-500 block mb-1">DSL Expression</label>
                          <input
                            type="text"
                            value={passwordExpression}
                            onChange={e => setPasswordExpression(e.target.value)}
                            placeholder="lower(first(name,4)) + ddmm(dob)"
                            className="w-full text-sm border rounded-lg px-2.5 py-2 font-mono"
                            data-testid="password-expression-input"
                          />
                          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                            <strong>Fields:</strong> <code>name</code>, <code>account_name</code>, <code>pan</code>, <code>dob</code>, <code>folio_no</code>, <code>ppm_no</code>, <code>mobile</code><br />
                            <strong>Functions:</strong> <code>lower(x)</code>, <code>upper(x)</code>, <code>first(x,n)</code>, <code>last(x,n)</code>, <code>digits(x)</code>, <code>ddmm(dob)</code>, <code>ddmmyyyy(dob)</code>, <code>yyyy(dob)</code><br />
                            <strong>Operator:</strong> <code>+</code> concatenates. Quoted literals supported, e.g. <code>"FUND-" + upper(pan)</code>
                          </p>
                        </div>
                      )}

                      {passwordMode === 'csv' && (
                        <div className="mt-2" data-testid="password-csv-block">
                          <label className="text-xs text-gray-500 block mb-1">Upload CSV / XLSX</label>
                          <input
                            type="file"
                            accept=".csv,.xlsx"
                            onChange={e => handlePasswordCsvUpload(e.target.files?.[0] || null)}
                            className="w-full text-sm"
                            data-testid="password-csv-input"
                          />
                          {passwordCsvFile && (
                            <p className="text-xs text-green-700 mt-1">
                              <Upload size={11} className="inline mr-1" /> {passwordCsvFile.name} ({Math.round(passwordCsvFile.size / 1024)} KB) ready to upload
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                            Columns: any of <code>account_id</code>, <code>account_name</code>, <code>pan</code>, <code>folio_no</code>, <code>ppm_no</code> + a <code>password</code> column. Match is case-insensitive.
                          </p>
                        </div>
                      )}

                      {passwordMode !== 'none' && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                          Both protected and unprotected PDFs will be saved. Investors with missing fields are generated without encryption (warning shown on completion).
                          {' '}Set the investor-facing password hint in the <span className="font-medium">Summary</span> tab — it ships as <code className="bg-amber-100 px-1 rounded">{`{{password_hint}}`}</code> in your email + WhatsApp templates.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Investor Selection + Generate */}
              <div className="lg:col-span-2">
                {!selectedTemplate ? (
                  <div className="bg-white border rounded-lg p-8 text-center text-gray-400">
                    <FileOutput size={40} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Select a template from the left panel to generate drawdown notices.</p>
                  </div>
                ) : (
                  <div className="bg-white border rounded-lg">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800">
                          Generate: <span className="text-blue-600">{selectedTemplate.name}</span>
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">Select investors, configure options, then generate notices</p>
                      </div>
                      <button onClick={handleGenerateNotices}
                        disabled={generating || selectedInvestorIds.length === 0}
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700 inline-flex items-center gap-1.5"
                        data-testid="generate-dd-docs-btn">
                        {generating ? (
                          <><Loader2 size={14} className="animate-spin" /> Generating...</>
                        ) : (
                          <><FileOutput size={14} /> Generate {selectedInvestorIds.length > 0 ? `(${selectedInvestorIds.length})` : ''}</>
                        )}
                      </button>
                    </div>

                    {/* Generation Progress Bar */}
                    {generating && generationJob && (
                      <div className="px-4 py-3 border-b bg-blue-50" data-testid="generation-progress">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">
                              {generationJob.abort_requested ? 'Aborting...' : 'Generating notices...'} {generationJob.completed || 0} of {generationJob.total || 0} done
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-blue-600">
                              {generationJob.failed > 0 && <span className="text-red-500 mr-2">{generationJob.failed} failed</span>}
                              {generationJob.total > 0 ? Math.round((((generationJob.completed || 0) + (generationJob.failed || 0)) / generationJob.total) * 100) : 0}%
                            </span>
                            {!generationJob.abort_requested && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Abort the current generation? Notices already generated will be kept.')) return;
                                  try {
                                    await drawdownAPI.abortGeneration(id);
                                    // Optimistically reflect; the next poll will update the official status
                                    setGenerationJob(prev => prev ? { ...prev, abort_requested: true } : prev);
                                  } catch { alert('Failed to send abort request'); }
                                }}
                                className="text-xs px-2 py-1 border border-red-300 text-red-600 hover:bg-red-50 rounded"
                                data-testid="abort-generation-btn"
                                title="Stop after the current batch"
                              >
                                Abort
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden flex">
                          <div
                            className="bg-blue-600 h-2 transition-all duration-500"
                            style={{ width: `${generationJob.total > 0 ? ((generationJob.completed || 0) / generationJob.total) * 100 : 0}%` }}
                          />
                          {generationJob.failed > 0 && (
                            <div
                              className="bg-red-400 h-2 transition-all duration-500"
                              style={{ width: `${(generationJob.failed / generationJob.total) * 100}%` }}
                            />
                          )}
                        </div>
                        <p className="text-xs text-blue-600 mt-1.5">
                          Each document is generated as DOCX and converted to PDF via LibreOffice. This may take a few minutes for large batches.
                        </p>
                        {/* Show failures during generation */}
                        {generationJob.failed_accounts && generationJob.failed_accounts.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {generationJob.failed_accounts.map((acct, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded px-2 py-1">
                                <AlertTriangle size={11} className="text-red-400 flex-shrink-0" />
                                <span className="font-medium">{acct.account_name}</span>
                                {acct.class_name && <span className="text-red-500">({acct.class_name})</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-3 border-b flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
                        <input type="text" value={docSearch} onChange={e => setDocSearch(e.target.value)}
                          placeholder="Search investors..." className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg" data-testid="doc-investor-search" />
                      </div>
                      <button onClick={toggleSelectAllInvestors}
                        className="px-3 py-2 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-50 whitespace-nowrap" data-testid="doc-select-all-btn">
                        {filteredDocInvestors.length > 0 && filteredDocInvestors.every(i => selectedInvestorIds.includes(i.id)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {filteredDocInvestors.map(inv => {
                        const isSelected = selectedInvestorIds.includes(inv.id);
                        const outstanding = (inv.drawdown_amount || 0) - (inv.received_amount || 0);
                        return (
                          <label key={inv.id}
                            className={`flex items-center gap-3 px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}
                            data-testid={`doc-inv-${inv.id}`}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleInvestorSelection(inv.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{inv.account_name}</span>
                                {inv.class_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{inv.class_name}</span>}
                              </div>
                              <span className="text-xs text-gray-500">
                                {inv.folio_no && `Folio: ${inv.folio_no}`}{inv.folio_no && inv.ppm_no && ' | '}{inv.ppm_no && `PPM: ${inv.ppm_no}`}
                              </span>
                            </div>
                            <div className="text-right text-xs">
                              <p className="text-gray-600">DD: {(inv.drawdown_amount || 0).toLocaleString()}</p>
                              <p className={outstanding > 0 ? 'text-red-500' : 'text-green-600'}>
                                {outstanding > 0 ? `Out: ${outstanding.toLocaleString()}` : 'Paid'}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    <div className="p-3 bg-gray-50 rounded-b-lg border-t text-xs text-gray-500">
                      {selectedInvestorIds.length} of {investors.length} selected &middot; {selectedTemplate.name} ({selectedTemplate.file_type.toUpperCase()})
                      {selectedTemplate.file_type === 'docx' && ' &middot; Output: DOCX + PDF'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generated Notices View */}
          {docView === 'notices' && (
            <div>
              {/* Generation job result banner */}
              {generationJob && generationJob.status !== 'running' && generationJob.status !== 'idle' && (
                <div className={`mb-4 rounded-lg border overflow-hidden ${
                  generationJob.status === 'completed' ? 'bg-green-50 border-green-200' :
                  generationJob.status === 'partial' ? 'bg-amber-50 border-amber-200' :
                  'bg-red-50 border-red-200'
                }`} data-testid="generation-result-banner">
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {generationJob.status === 'completed' ? (
                        <CheckCircle size={16} className="text-green-600" />
                      ) : (
                        <AlertTriangle size={16} className={generationJob.status === 'partial' ? 'text-amber-600' : 'text-red-600'} />
                      )}
                      <span className={`text-sm font-medium ${
                        generationJob.status === 'completed' ? 'text-green-800' :
                        generationJob.status === 'partial' ? 'text-amber-800' : 'text-red-800'
                      }`}>
                        {generationJob.status === 'completed' && `All ${generationJob.completed} notices generated successfully`}
                        {generationJob.status === 'partial' && `${generationJob.completed} of ${generationJob.total} generated (${generationJob.failed} failed)`}
                        {generationJob.status === 'failed' && `Generation failed for all ${generationJob.total} notices`}
                      </span>
                    </div>
                    <button onClick={() => setGenerationJob(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                  </div>

                  {/* Failed accounts list with retry */}
                  {generationJob.failed_accounts && generationJob.failed_accounts.length > 0 && (
                    <div className="px-3 pb-3 border-t border-amber-200" data-testid="failed-accounts-list">
                      <div className="mt-2 mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Failed Accounts</span>
                        {selectedTemplate && (
                          <button
                            onClick={handleRetryFailed}
                            disabled={generating}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                            data-testid="retry-failed-btn"
                          >
                            <RefreshCw size={12} /> Retry {generationJob.failed_accounts.length} Failed
                          </button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {generationJob.failed_accounts.map((acct, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-amber-900 bg-amber-100/50 rounded px-2 py-1.5">
                            <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
                            <span className="font-medium">{acct.account_name}</span>
                            {acct.class_name && <span className="text-amber-600">({acct.class_name})</span>}
                            {acct.error && <span className="text-amber-500 ml-auto truncate max-w-[300px]" title={acct.error}>{acct.error}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Encryption-skipped warnings */}
                  {generationJob.encryption_skipped && generationJob.encryption_skipped.length > 0 && (
                    <div className="px-3 pb-3 border-t border-amber-200" data-testid="encryption-skipped-list">
                      <div className="mt-2 mb-2">
                        <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide inline-flex items-center gap-1">
                          <Lock size={11} /> Password Could Not Be Resolved ({generationJob.encryption_skipped.length})
                        </span>
                        <p className="text-[11px] text-amber-700 mt-0.5">These investors received unprotected PDFs only. Fix the source data (e.g. PAN, DOB) and regenerate, or use a different password mode.</p>
                      </div>
                      <div className="space-y-1">
                        {generationJob.encryption_skipped.map((acct, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-amber-900 bg-amber-100/50 rounded px-2 py-1.5">
                            <Lock size={11} className="text-amber-500 flex-shrink-0" />
                            <span className="font-medium">{acct.account_name}</span>
                            {acct.class_name && <span className="text-amber-600">({acct.class_name})</span>}
                            {acct.reason && <span className="text-amber-500 ml-auto truncate max-w-[300px]" title={acct.reason}>{acct.reason}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {loadingNotices ? (
                <div className="text-center py-8 text-gray-400"><Loader2 size={20} className="animate-spin inline mr-2" /> Loading notices...</div>
              ) : notices.length === 0 ? (
                <div className="bg-white border rounded-lg p-8 text-center text-gray-400">
                  <FileText size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No notices generated yet. Switch to "Generate Notices" to create them.</p>
                </div>
              ) : (
                <div className="bg-white border rounded-lg overflow-hidden">
                  {/* Header with bulk actions */}
                  <div className="px-4 py-3 border-b bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">{notices.length} Notice{notices.length !== 1 ? 's' : ''} Generated</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> {notices.filter(n => n.reviewed).length} Reviewed</span>
                        <span className="inline-flex items-center gap-1"><Eye size={12} className="text-amber-500" /> {notices.filter(n => !n.reviewed).length} Pending</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      {/* Bulk downloads */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500 mr-1">Bulk Download:</span>
                        <button onClick={() => handleBulkDownload('docx')} disabled={bulkDownloading}
                          className="px-2.5 py-1.5 text-xs border rounded-md text-blue-600 hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid="bulk-download-docx">
                          {bulkDownloading === 'docx' ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} All DOCX
                        </button>
                        <button onClick={() => handleBulkDownload('pdf')} disabled={bulkDownloading}
                          className="px-2.5 py-1.5 text-xs border rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid="bulk-download-pdf">
                          {bulkDownloading === 'pdf' ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} All PDF
                        </button>
                        {notices.some(n => n.protected_pdf_storage_path) && (
                          <button onClick={() => handleBulkDownload('pdf', true)} disabled={bulkDownloading}
                            className="px-2.5 py-1.5 text-xs border border-amber-300 rounded-md text-amber-700 hover:bg-amber-50 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="bulk-download-pdf-protected">
                            {bulkDownloading === 'pdf-protected' ? <Loader2 size={11} className="animate-spin" /> : <Lock size={11} />} All PDF (Protected)
                          </button>
                        )}
                      </div>
                      {/* Bulk review */}
                      <div className="flex items-center gap-2">
                        {selectedNoticeIds.length > 0 && (
                          <button onClick={handleBulkDeleteNotices} disabled={bulkDeletingNotices}
                            className="px-2.5 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="bulk-delete-notices-btn">
                            {bulkDeletingNotices ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                            Delete Selected ({selectedNoticeIds.length})
                          </button>
                        )}
                        {selectedNoticeIds.length > 0 && selectedNoticeIds.some(nid => { const n = notices.find(x => x.id === nid); return n && !n.reviewed; }) && (
                          <button onClick={handleBulkReview} disabled={bulkReviewing}
                            className="px-2.5 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="bulk-review-btn">
                            {bulkReviewing ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                            Review Selected ({selectedNoticeIds.filter(nid => { const n = notices.find(x => x.id === nid); return n && !n.reviewed; }).length})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Select All row */}
                  <div className="px-4 py-2 border-b bg-white flex items-center gap-3">
                    <input type="checkbox"
                      checked={notices.length > 0 && notices.every(n => selectedNoticeIds.includes(n.id))}
                      onChange={toggleSelectAllNotices}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid="select-all-notices" />
                    <span className="text-xs text-gray-500">
                      {selectedNoticeIds.length > 0 ? `${selectedNoticeIds.length} selected` : 'Select All'}
                    </span>
                  </div>

                  {/* Notice rows */}
                  <div className="divide-y">
                    {notices.map(notice => {
                      const isSelected = selectedNoticeIds.includes(notice.id);
                      return (
                        <div key={notice.id} className={`px-4 py-3 flex items-center gap-3 hover:bg-gray-50 ${isSelected ? 'bg-blue-50/30' : ''}`} data-testid={`notice-${notice.id}`}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleNoticeSelection(notice.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 truncate">{notice.file_name}</span>
                              {notice.class_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{notice.class_name}</span>}
                              {notice.is_encrypted && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-medium inline-flex items-center gap-1" title={`Mode: ${notice.password_mode || ''}`} data-testid={`notice-encrypted-${notice.id}`}>
                                  <Lock size={10} /> Password Protected
                                </span>
                              )}
                              {notice.reviewed ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium" data-testid={`notice-reviewed-${notice.id}`}>Reviewed</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">Pending Review</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {notice.account_name} &middot; {notice.template_name} &middot; {new Date(notice.generated_at).toLocaleDateString()}
                              {notice.reviewed_by && ` · Reviewed by ${notice.reviewed_by}`}
                              {notice.is_encrypted && notice.password && (
                                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded font-mono" title="Password used to encrypt this PDF" data-testid={`notice-password-${notice.id}`}>
                                  <Lock size={9} /> {notice.password}
                                </span>
                              )}
                            </p>
                            {(notice.short_url_pdf || notice.short_url_protected_pdf) && (() => {
                              // Backend returns absolute URLs when BACKEND_PUBLIC_URL is configured;
                              // in preview environments it falls back to a `/drawdown/{code}` path —
                              // prepend the current origin so the copy-to-clipboard value is always
                              // an investor-shareable absolute URL.
                              const toAbs = (u) => {
                                if (!u) return '';
                                if (/^https?:\/\//i.test(u)) return u;
                                if (typeof window !== 'undefined' && window.location) {
                                  return `${window.location.origin}${u.startsWith('/') ? '' : '/'}${u}`;
                                }
                                return u;
                              };
                              const protectedAbs = toAbs(notice.short_url_protected_pdf);
                              const pdfAbs = toAbs(notice.short_url_pdf);
                              return (
                              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`notice-short-links-${notice.id}`}>
                                {protectedAbs && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(protectedAbs);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 max-w-full"
                                    title={`Click to copy — Protected PDF link\n${protectedAbs}`}
                                    data-testid={`copy-short-link-protected-${notice.id}`}
                                  >
                                    <Lock size={10} />
                                    <span className="font-medium">Protected link</span>
                                    <span className="font-mono truncate max-w-[180px] text-amber-700">
                                      {protectedAbs.replace(/^https?:\/\//, '')}
                                    </span>
                                    <Copy size={10} className="opacity-60" />
                                  </button>
                                )}
                                {pdfAbs && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(pdfAbs);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 max-w-full"
                                    title={`Click to copy — PDF link\n${pdfAbs}`}
                                    data-testid={`copy-short-link-pdf-${notice.id}`}
                                  >
                                    <Link2 size={10} />
                                    <span className="font-medium">PDF link</span>
                                    <span className="font-mono truncate max-w-[180px] text-blue-700">
                                      {pdfAbs.replace(/^https?:\/\//, '')}
                                    </span>
                                    <Copy size={10} className="opacity-60" />
                                  </button>
                                )}
                              </div>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-1">
                            {notice.docx_storage_path && (
                              <button onClick={() => handleDownloadNotice(notice, 'docx')}
                                className="px-2 py-1 text-xs border rounded-md text-blue-600 hover:bg-blue-50" title="Download DOCX" data-testid={`dl-docx-${notice.id}`}>
                                <Download size={12} className="inline mr-1" />DOCX
                              </button>
                            )}
                            {notice.docx_storage_path && (
                              <button onClick={() => handleDownloadNotice(notice, 'pdf')}
                                className={`px-2 py-1 text-xs border rounded-md hover:bg-red-50 ${notice.pdf_storage_path ? 'text-red-600' : 'text-red-400'}`} title={notice.pdf_storage_path ? "Download original (unprotected) PDF" : "Generate & Download PDF"} data-testid={`dl-pdf-${notice.id}`}>
                                <Download size={12} className="inline mr-1" />PDF
                              </button>
                            )}
                            {notice.protected_pdf_storage_path && (
                              <button onClick={() => handleDownloadNotice(notice, 'pdf', true)}
                                className="px-2 py-1 text-xs border border-amber-300 rounded-md text-amber-700 hover:bg-amber-50 inline-flex items-center" title="Download password-protected PDF" data-testid={`dl-pdf-protected-${notice.id}`}>
                                <Lock size={11} className="inline mr-1" />Protected
                              </button>
                            )}
                            {!notice.reviewed && (
                              <button onClick={() => handleReviewNotice(notice.id)}
                                className="px-2 py-1 text-xs border rounded-md text-green-600 hover:bg-green-50" title="Mark as Reviewed" data-testid={`review-${notice.id}`}>
                                <CheckCircle size={12} className="inline mr-1" />Review
                              </button>
                            )}
                            <button onClick={() => handleDeleteNotice(notice.id)}
                              className="px-2 py-1 text-xs border rounded-md text-gray-400 hover:bg-gray-50 hover:text-red-500" title="Delete" data-testid={`del-notice-${notice.id}`}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== Emails Tab (Phase C) ===== */}
      {activeTab === 'emails' && (
        <div data-testid="tab-content-emails">
          <DrawdownEmailsTab drawdownId={id} investors={investors} currentUser={user} />
        </div>
      )}

      {/* ===== Reminders Tab (Phase G) ===== */}
      {activeTab === 'reminders' && (
        <div data-testid="tab-content-reminders">
          <DrawdownRemindersTab drawdownId={id} currentUser={user} />
        </div>
      )}

      {/* ===== Acknowledgments Tab (Phase C) ===== */}
      {activeTab === 'acknowledgments' && (
        <div data-testid="tab-content-acknowledgments">
          <DrawdownAckTab drawdownId={id} currentUser={user} />
        </div>
      )}

      {/* ===== Add Investor Modal (Phase M2 — maker-checker) ===== */}
      {showAddInvestorModal && (() => {
        const isClasswise = drawdown?.class_percentages && drawdown.class_percentages.length > 0;
        const selectedAcct = eligibleAccounts.find(a => a.id === addInvForm.account_id);
        // Classes already used on the drawdown for THIS account
        const usedClassesForAcct = new Set(
          investors
            .filter(i => i.account_id === addInvForm.account_id && i.pending_action !== 'delete')
            .map(i => i.class_name || '')
        );
        const availableClasses = (selectedAcct?._classes_in_fund || []).filter(cn => !usedClassesForAcct.has(cn));
        const needsClass = isClasswise || (selectedAcct?._classes_in_fund || []).length > 0;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-investor-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Investor to Drawdown</h3>
                <button onClick={() => setShowAddInvestorModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                The selected investor account will be added as a pending request. A different user with Manager/Admin role must approve before the row counts toward the drawdown totals.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Investor Account</label>
                  <select
                    value={addInvForm.account_id}
                    onChange={e => {
                      const acc = eligibleAccounts.find(a => a.id === e.target.value);
                      const free = (acc?._classes_in_fund || []).filter(cn => !investors.some(i => i.account_id === acc.id && i.pending_action !== 'delete' && (i.class_name || '') === cn));
                      setAddInvForm(p => ({ ...p, account_id: e.target.value, class_name: free.length === 1 ? free[0] : '' }));
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                    data-testid="add-inv-account-select"
                  >
                    <option value="">-- Select an account --</option>
                    {eligibleAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.account_name}{a.pan ? ` · ${a.pan}` : ''}</option>
                    ))}
                  </select>
                  {eligibleAccounts.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No eligible accounts. Every account that holds this fund is already on the drawdown.</p>
                  )}
                </div>
                {needsClass && selectedAcct && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Class</label>
                    <select
                      value={addInvForm.class_name}
                      onChange={e => setAddInvForm(p => ({ ...p, class_name: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                      data-testid="add-inv-class-select"
                    >
                      <option value="">-- Select a class --</option>
                      {availableClasses.map(cn => <option key={cn} value={cn}>{cn}</option>)}
                    </select>
                    {availableClasses.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">This account's classes are all already on the drawdown.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 mt-5">
                <button onClick={() => setShowAddInvestorModal(false)} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
                <button
                  onClick={submitAddInvestor}
                  disabled={addInvSubmitting || !addInvForm.account_id || (needsClass && !addInvForm.class_name)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="add-inv-submit-btn"
                >
                  {addInvSubmitting ? 'Submitting…' : 'Submit for Approval'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== Record Receipt Modal ===== */}
      {receiptModal && (() => {
        const matchedClass = fundClasses.find(c => c.name === receiptModal.class_name);
        const faceValue = matchedClass?.face_value || 0;
        const amountNum = parseFloat(receiptForm.amount) || 0;
        const computedUnits = faceValue > 0 && amountNum > 0
          ? (amountNum / faceValue).toFixed(3)
          : '';
        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="receipt-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Record Receipt</h3>
              <button onClick={() => setReceiptModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-1">
              <strong>{receiptModal.account_name}</strong>
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Drawdown: {(receiptModal.drawdown_amount || 0).toLocaleString()} | Already received: {(receiptModal.received_amount || 0).toLocaleString()} | Outstanding: {((receiptModal.drawdown_amount || 0) - (receiptModal.received_amount || 0)).toLocaleString()}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Amount *</label>
                <input type="number" value={receiptForm.amount} onChange={e => setReceiptForm(p => ({ ...p, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="receipt-amount-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date of Receipt *</label>
                <input type="date" value={receiptForm.date_of_receipt} onChange={e => setReceiptForm(p => ({ ...p, date_of_receipt: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="receipt-date-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Class of Units</label>
                  <input type="text" value={receiptModal.class_name || ''} readOnly
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-700 cursor-not-allowed"
                    data-testid="receipt-class-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">ISIN</label>
                  <input type="text" value={matchedClass?.isin || '-'} readOnly
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-700 cursor-not-allowed font-mono"
                    data-testid="receipt-isin-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Units</label>
                <input type="text" value={computedUnits} readOnly
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-700 cursor-not-allowed font-mono"
                  data-testid="receipt-units-input" />
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Auto-computed = Amount ÷ Face Value ({faceValue ? faceValue.toLocaleString() : '—'}), rounded to 3 decimals.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setReceiptModal(null)} className="px-3 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleRecordReceipt} disabled={submittingReceipt} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg disabled:opacity-50" data-testid="confirm-receipt-btn">
                {submittingReceipt ? 'Recording...' : 'Confirm Receipt'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ===== Edit Receipt Modal ===== */}
      {editingReceipt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-receipt-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Receipt</h3>
              <button onClick={() => setEditingReceipt(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-sm font-medium text-gray-700">Amount</label>
                <input type="number" value={editReceiptForm.amount} onChange={e => setEditReceiptForm(p => ({ ...p, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="edit-receipt-amount" /></div>
              <div><label className="text-sm font-medium text-gray-700">Date of Receipt</label>
                <input type="date" value={editReceiptForm.date_of_receipt} onChange={e => setEditReceiptForm(p => ({ ...p, date_of_receipt: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="edit-receipt-date" /></div>
              <div><label className="text-sm font-medium text-gray-700">SOA Remarks</label>
                <textarea value={editReceiptForm.soa_remarks} onChange={e => setEditReceiptForm(p => ({ ...p, soa_remarks: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="edit-receipt-remarks" /></div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setEditingReceipt(null)} className="px-3 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleEditReceipt} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-edit-receipt-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {/* ===== Reject Modal (Maker-Checker) ===== */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-700">Reject {rejectModal.kind === 'drawdown' ? 'Drawdown' : 'Receipt'}</h3>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">Provide a reason so the maker can fix and re-submit.</p>
            <textarea value={rejectModal.reason} onChange={e => setRejectModal(p => ({ ...p, reason: e.target.value }))}
              rows={3} placeholder="Reason for rejection (optional but recommended)"
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="reject-reason-input" />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setRejectModal(null)} className="px-3 py-2 text-sm border rounded-lg">Cancel</button>
              <button
                onClick={rejectModal.kind === 'drawdown' ? handleRejectDrawdown : handleRejectReceipt}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg disabled:opacity-50"
                data-testid="confirm-reject-btn">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawdownDetail;
